import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle, useMemo, useCallback } from 'react';
import { Stage, Layer, Image as KonvaImage, Line, Circle, Rect, Group, Text } from 'react-konva';
import { TOOLS } from './DrawingTools';
import Token from './Token';
import { useLayers } from '../../context/LayerContext';
import { useHistory, COMMAND_TYPES } from '../../context/HistoryContext';
import { useContainers } from '../../context/ContainerContext';
import { LineBasedFOVAdapter } from './models';
import MapContainer from './MapContainer';
// ContainerHoverMenu import removed - functionality moved to MapContainer.js
import { convertPointsToGlobal } from '../../utils/coordinateUtils';
import './Map.css';
// Legacy imports - will be removed when migration is complete
// import useImage from 'use-image';
// import { Menu, Item, useContextMenu } from 'react-contexify';
// import 'react-contexify/dist/ReactContexify.css';

// Helper to enable/disable the new line-based FOV system, can be changed as needed
const DEFAULT_USE_LINE_BASED_FOV = true; // <--- Using the new system by default

// Local FOV class removed - unused implementation
// The system now uses LineBasedFOV and LineBasedFOVAdapter from './models'

// FOV quality is now fixed at MEDIUM (720 rays) - removed quality options

// Helper functions for eraser functionality - moved to file scope for stable references
  function segmentCircleIntersections(x1, y1, x2, y2, cx, cy, r) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const fx = x1 - cx;
    const fy = y1 - cy;
    const a = dx * dx + dy * dy;
    const b = 2 * (fx * dx + fy * dy);
    const c = fx * fx + fy * fy - r * r;
    const discriminant = b * b - 4 * a * c;
    if (discriminant < 0) return [];
    const sqrtDiscriminant = Math.sqrt(discriminant);
    const t1 = (-b - sqrtDiscriminant) / (2 * a);
    const t2 = (-b + sqrtDiscriminant) / (2 * a);
    return [t1, t2].filter(t => t >= 0 && t <= 1);
  }

function clipWallSegmentByEraser(line, eraserLine, eraserRadius) {
  if (!line.points || line.points.length < 4) return [];
  const [x1, y1, x2, y2] = line.points;
  let cutTs = [0, 1];
  for (let i = 0; i < eraserLine.points.length; i += 2) {
    const ex = eraserLine.points[i];
    const ey = eraserLine.points[i + 1];
    const ts = segmentCircleIntersections(x1, y1, x2, y2, ex, ey, eraserRadius);
    cutTs.push(...ts);
  }
  cutTs = Array.from(new Set(cutTs.filter(t => t >= 0 && t <= 1))).sort((a, b) => a - b);
  const result = [];
  let segmentIndex = 0;
  for (let i = 0; i < cutTs.length - 1; i++) {
    const t0 = cutTs[i];
    const t1 = cutTs[i + 1];
    if (t1 - t0 < 0.01) continue;
    const mx = x1 + (x2 - x1) * (t0 + t1) / 2;
    const my = y1 + (y2 - y1) * (t0 + t1) / 2;
    let inside = false;
    for (let j = 0; j < eraserLine.points.length; j += 2) {
      const ex = eraserLine.points[j];
      const ey = eraserLine.points[j + 1];
      if (Math.sqrt((mx - ex) ** 2 + (my - ey) ** 2) <= eraserRadius) {
        inside = true;
        break;
      }
    }
    if (!inside) {
      const sx = x1 + (x2 - x1) * t0;
      const sy = y1 + (y2 - y1) * t0;
      const exx = x1 + (x2 - x1) * t1;
      const eyy = y1 + (y2 - y1) * t1;
      result.push({ 
        ...line, 
        id: `${line.id}_segment_${segmentIndex++}`,
        points: [sx, sy, exx, eyy],
        originalId: line.id,
        isClippedSegment: true
      });
    }
  }
  return result;
}

function segmentNearAnyEraserCircle(x1, y1, x2, y2, eraserPoints, eraserRadius) {
  const minX = Math.min(x1, x2) - eraserRadius;
  const maxX = Math.max(x1, x2) + eraserRadius;
  const minY = Math.min(y1, y2) - eraserRadius;
  const maxY = Math.max(y1, y2) + eraserRadius;
  for (let i = 0; i < eraserPoints.length; i += 2) {
    const ex = eraserPoints[i];
    const ey = eraserPoints[i + 1];
    if (ex >= minX && ex <= maxX && ey >= minY && ey <= maxY) {
      return true;
    }
  }
  return false;
}

function clipLineByEraser(line, eraserLine, eraserRadius) {
  if (!line.points || line.points.length < 4) return [];
  const segments = [];
  for (let i = 0; i < line.points.length - 2; i += 2) {
    const x1 = line.points[i];
    const y1 = line.points[i + 1];
    const x2 = line.points[i + 2];
    const y2 = line.points[i + 3];
    if (!segmentNearAnyEraserCircle(x1, y1, x2, y2, eraserLine.points, eraserRadius)) {
      segments.push([x1, y1, x2, y2]);
      continue;
    }
    let cutTs = [0, 1];
    for (let j = 0; j < eraserLine.points.length; j += 2) {
      const ex = eraserLine.points[j];
      const ey = eraserLine.points[j + 1];
      const ts = segmentCircleIntersections(x1, y1, x2, y2, ex, ey, eraserRadius);
      cutTs.push(...ts);
    }
    cutTs = Array.from(new Set(cutTs.filter(t => t >= 0 && t <= 1))).sort((a, b) => a - b);
    for (let k = 0; k < cutTs.length - 1; k++) {
      const t0 = cutTs[k];
      const t1 = cutTs[k + 1];
      if (t1 - t0 < 0.01) continue;
      const mx = x1 + (x2 - x1) * (t0 + t1) / 2;
      const my = y1 + (y2 - y1) * (t0 + t1) / 2;
      let inside = false;
      for (let j = 0; j < eraserLine.points.length; j += 2) {
        const ex = eraserLine.points[j];
        const ey = eraserLine.points[j + 1];
        if (Math.sqrt((mx - ex) ** 2 + (my - ey) ** 2) <= eraserRadius) {
          inside = true;
          break;
        }
      }
      if (!inside) {
        const sx = x1 + (x2 - x1) * t0;
        const sy = y1 + (y2 - y1) * t0;
        const exx = x1 + (x2 - x1) * t1;
        const eyy = y1 + (y2 - y1) * t1;
        segments.push([sx, sy, exx, eyy]);
      }
    }
  }
  const result = [];
  let current = [];
  let segmentIndex = 0;
  
  for (let i = 0; i < segments.length; i++) {
    const [x1, y1, x2, y2] = segments[i];
    if (current.length === 0) {
      current.push(x1, y1, x2, y2);
    } else {
      const lastX = current[current.length - 2];
      const lastY = current[current.length - 1];
      if (Math.abs(x1 - lastX) < 1e-6 && Math.abs(y1 - lastY) < 1e-6) {
        current.push(x2, y2);
      } else {
        if (current.length >= 4) {
          result.push({ 
            ...line, 
            id: `${line.id}_segment_${segmentIndex++}`,
            points: [...current],
            originalId: line.id,
            isClippedSegment: true
          });
        }
        current = [x1, y1, x2, y2];
      }
    }
  }
  if (current.length >= 4) {
    result.push({ 
      ...line, 
      id: `${line.id}_segment_${segmentIndex}`,
      points: [...current],
      originalId: line.id,
      isClippedSegment: true
    });
  }
  return result;
}

function eraseLinesDestructive(lines, eraserLine, eraserRadius) {
  let result = [];
  
  for (const line of lines) {
    if (line.tool === 'eraser') continue;
    // מחיקה מלאה של עיגול
    if (line.tool === TOOLS.CIRCLE && line.x !== undefined && line.y !== undefined && line.radius !== undefined) {
      let shouldErase = false;
      for (let i = 0; i < eraserLine.points.length; i += 2) {
        const ex = eraserLine.points[i];
        const ey = eraserLine.points[i + 1];
        const dist = Math.sqrt((ex - line.x) ** 2 + (ey - line.y) ** 2);
        if (dist <= line.radius + eraserRadius) {
          shouldErase = true;
          break;
        }
      }
      if (shouldErase) continue; // מוחק את כל העיגול
      result.push(line);
      continue;
    }
    // מחיקה מלאה של ריבוע
    if (line.tool === TOOLS.RECTANGLE && line.x !== undefined && line.y !== undefined && line.width !== undefined && line.height !== undefined) {
      let shouldErase = false;
      for (let i = 0; i < eraserLine.points.length; i += 2) {
        const ex = eraserLine.points[i];
        const ey = eraserLine.points[i + 1];
        if (
          ex >= line.x - eraserRadius &&
          ex <= line.x + line.width + eraserRadius &&
          ey >= line.y - eraserRadius &&
          ey <= line.y + line.height + eraserRadius
        ) {
          shouldErase = true;
          break;
        }
      }
      if (shouldErase) continue; // מוחק את כל הריבוע
      result.push(line);
      continue;
    }
    // שאר הצורות - מחיקה רגילה
    if (!line.points || line.points.length < 4) {
      result.push(line);
      continue;
    }
    
    // זיהוי נכון של סוג הכלי במקום הסתמכות על מספר נקודות
    if (line.tool === TOOLS.WALL) {
      // Wall segment - תמיד 2 נקודות (4 ערכים)
      const clipped = clipWallSegmentByEraser(line, eraserLine, eraserRadius);
      result.push(...clipped);
    } else if (line.tool === TOOLS.PENCIL) {
      // Pencil line - יכול להיות 2+ נקודות
      const clipped = clipLineByEraser(line, eraserLine, eraserRadius);
      result.push(...clipped);
    } else {
      // כלים אחרים עם נקודות (פוליגון וכו')
      const clipped = clipLineByEraser(line, eraserLine, eraserRadius);
      result.push(...clipped);
    }
  }
  
  return result;
}

// Main MapDisplay component
const MapDisplay = forwardRef(({
  tokens: propTokens, // Renamed from tokens to propTokens
  activeTool, 
  toolOptions, 
  onTokenSelect, 
  onNewTokenCreated, // Added prop
  selectedTokenIds, 
  mapId, 
  isEditMode = true,
  onUpdateToken,
  areDrawingsVisible, // New prop
  onOptionChange, // Added prop
  onToolChange, // Added prop for tool switching
  groupLightingEnabled = false // Added prop for group lighting
}, ref) => {
  const [mapImage] = useState(null); // setMapImage removed - functionality replaced by Container system
  const [stageScale, setStageScale] = useState(1);
  const [stagePosition, setStagePosition] = useState({ x: 0, y: 0 });
  const [stageSize, setStageSize] = useState({ width: 800, height: 600 });
  const [isDrawing, setIsDrawing] = useState(false);
  
  // Get layer context
    const {
    layers, 
    // activeLayerId, 
    getActiveLayer,
    updateLayer,
    // loading,
    // createImageLayer, // Removed - unused in this component, handled by LayerManager.js
    // createImageLayerBelowActive, // Removed - specialized FOW function not yet implemented
    shouldAutoLock
    // getLayerGroup // Removed - unused in this component, handled by LayerManager.js
  } = useLayers();
  
  // Get history context
  const { pushCommand, undo, redo } = useHistory();
  
  // Get container context
  const {
    containers,
    selectedContainerId,
    // activeContainerForDrawing,
    hoveredContainerId,
    // hoverMenuPosition, // Removed - positioning moved to relative coordinates in MapContainer.js
    addContainer,
    updateContainer,
    deleteContainer,
    // addDrawingToContainer, // Drawings-on-container disabled
    // updateDrawingInContainer, // Drawings-on-container disabled
    // deleteDrawingFromContainer, // Drawings-on-container disabled
    setSelectedContainerId,
    // setActiveContainerForDrawing,
    showHoverMenu,
    hideHoverMenu,
    getContainersByLayer,
    bringToFront,
    sendToBack,
    duplicateContainer,
    toggleLock
  } = useContainers();
  
  // Drawing state
  const [currentLine, setCurrentLine] = useState(null);
  const [wallPoints, setWallPoints] = useState([]);
  const [currentMousePos, setCurrentMousePos] = useState({ x: 0, y: 0 });
  const lastEraserApplyTimeRef = useRef(0);
  const eraserStartStateRef = useRef(null); // Store state before erasing starts
  // State for new shapes
  const [currentPolygonPoints, setCurrentPolygonPoints] = useState([]);
  const [currentRectangleStartPoint, setCurrentRectangleStartPoint] = useState(null);
  const [currentCircleCenter, setCurrentCircleCenter] = useState(null);
  const [currentCircleRadius, setCurrentCircleRadius] = useState(0);
  // Add state for eraser preview
  const [showEraserPreview, setShowEraserPreview] = useState(false);
  // Removed local tokens state: const [tokens, setTokens] = useState([]);
  
  // *** FOG OF WAR - SIMPLIFIED STATE ***
  // Fog of War is always enabled in play mode, disabled in edit mode
  const [fowVisible, setFowVisible] = useState(!isEditMode);
  // Add a loading state for fog calculations
  const [fowLoading, setFowLoading] = useState(false);
  // Add a cover state for initial transition
  const [initialFogCover, setInitialFogCover] = useState(false);
  
  // Add isTransitioning to track when we're switching between modes
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // We need a render trigger for the fog of war
  const [fowRenderTrigger, setFowRenderTrigger] = useState(0);
  
  // *** REFS TO PREVENT RECREATION ***
  const stageRef = useRef(null);
  const containerRef = useRef(null);
  const rafRef = useRef(null); // For requestAnimationFrame
  const activeTokenRef = useRef(null); // Store the active token
  const wallsRef = useRef([]); // Store the walls
  const losObjectsRef = useRef([]); // Store LoS objects with special behavior
  const visiblePointsRef = useRef([]); // Store the visibility polygon
  const lastUpdateTimeRef = useRef(0); // For throttling updates
  const tokenPositionRef = useRef({ x: 0, y: 0 }); // Track token position for renders
  const prevEditModeRef = useRef(undefined); // Track edit mode changes - start as undefined
  const draggingTokenIdRef = useRef(null); // NEW: Track ID of token being dragged
  
  // Add a state for the line-based FOV toggle
  const useLineBasedFOV = DEFAULT_USE_LINE_BASED_FOV; // Use new FOV system by default
  
  // Create an instance of the new line-based FOV system adapter
  const lineBasedFOVRef = useRef(null);
  if (!lineBasedFOVRef.current) {
    lineBasedFOVRef.current = new LineBasedFOVAdapter({ enabled: useLineBasedFOV });
  }
  
  // We need to track if the stage is currently being dragged
  const [isDraggingStage, setIsDraggingStage] = useState(false);
  
  // Fixed FOV quality setting (720 rays)
  const fovQuality = 'MEDIUM'; // Always use 720 rays for quality
  
  // Debug mode for FOV - controls removed, kept for potential developer debugging
  const showDebugFOV = false; // Constant - debug controls were removed
  
  // Add a new state for the current polygon lines
  const [currentPolygonLines, setCurrentPolygonLines] = useState([]);
  
  // State for rectangular selection
  const [isRectSelecting, setIsRectSelecting] = useState(false);
  const [rectSelectionStart, setRectSelectionStart] = useState(null);
  const [rectSelectionEnd, setRectSelectionEnd] = useState(null);
  const [rightClickStart, setRightClickStart] = useState(null); // Track right click start position
  
  // State for multi-token dragging
  const [dragStartPositions, setDragStartPositions] = useState({});
  // const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 }); // Removed - using local newOffset values instead
  // const [isDragging, setIsDragging] = useState(false); // Removed - isDragging state tracked but never read, only setter used
  
  // Add state to track the currently dragged token ID for useEffect dependencies
  const [currentlyDraggedTokenId, setCurrentlyDraggedTokenId] = useState(null);
  
  // Drawings-on-container disabled; always draw to layers
  
  // Legacy state - will be removed when migration is complete
  const [selectedImageId, setSelectedImageId] = useState(null);
  
  // *** FUNCTION DEFINITIONS - Make sure all functions are defined before useImperativeHandle ***

  // Calculate token positions with "black hole" effect for multi-token dragging
  const calculateTokenPositionsWithCollisionAvoidance = useCallback((
    selectedTokenIds, 
    dragStartPositions, 
    offset, 
    allTokens,
    mousePosition,
    draggedTokenId = null // The token being actively dragged
  ) => {
    const updatedTokens = [];
    
    // Calculate base positions using current token positions
    const basePositions = selectedTokenIds.map(tokenId => {
      const currentToken = allTokens.find(t => t.id === tokenId);
      if (!currentToken) return null;
      
      // For the dragged token, use the exact mouse position
      if (tokenId === draggedTokenId) {
        const startPos = dragStartPositions[tokenId];
        if (!startPos) return null;
        
        return {
          id: tokenId,
          x: startPos.x + offset.x,
          y: startPos.y + offset.y,
          radius: currentToken.radius || 15,
          isDraggedToken: true
        };
      }
      
      // For other tokens, use their current position (which includes previous physics updates)
      return {
        id: tokenId,
        x: currentToken.x,
        y: currentToken.y,
        radius: currentToken.radius || 15,
        isDraggedToken: false
      };
    }).filter(Boolean);
    
    // Find the dragged token position (the "black hole center")
    const draggedToken = basePositions.find(token => token.isDraggedToken);
    
    // Apply "black hole" physics
    basePositions.forEach((token, index) => {
      let newX = token.x;
      let newY = token.y;
      
      // If this is the dragged token, it follows the mouse directly and doesn't get affected by others
      if (token.isDraggedToken) {
        updatedTokens.push({
          id: token.id,
          x: newX,
          y: newY
        });
        return;
      }
      
      // For other tokens, apply "black hole" attraction to the dragged token
      if (draggedToken) {
        const deltaX = draggedToken.x - token.x;
        const deltaY = draggedToken.y - token.y;
        const distanceToDraggedToken = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        
        // Black hole effect: stronger attraction when farther, weaker when closer
        if (distanceToDraggedToken > 0) {
          // Acceleration increases with distance (like gravity)
          const maxDistance = 50; // Maximum effective distance
          const normalizedDistance = Math.min(distanceToDraggedToken, maxDistance) / maxDistance;
          
          // Attraction force: stronger when far (up to 0.8), weaker when close (minimum 0.2)
          const attractionForce = 0.4 + (normalizedDistance * 0.6);
          
          // Apply attraction - increased force for more noticeable effect
          const attractionX = deltaX * attractionForce * 0.1; // Increased from 0.05 to 0.1
          const attractionY = deltaY * attractionForce * 0.1;
          
          newX += attractionX;
          newY += attractionY;
        }
        
        // Repulsion from the dragged token to prevent overlap - tokens get pushed to its edges
        const minDistFromDragged = token.radius + draggedToken.radius + 5;
        if (distanceToDraggedToken < minDistFromDragged && distanceToDraggedToken > 0) {
          const pushDistance = (minDistFromDragged - distanceToDraggedToken) * 0.8;
          const pushX = (-deltaX / distanceToDraggedToken) * pushDistance;
          const pushY = (-deltaY / distanceToDraggedToken) * pushDistance;
          newX += pushX;
          newY += pushY;
        }
      }
      
      // Repulsion from other non-dragged selected tokens
      basePositions.forEach((otherToken, otherIndex) => {
        if (index !== otherIndex && !otherToken.isDraggedToken) {
          const dx = newX - otherToken.x;
          const dy = newY - otherToken.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const minDist = token.radius + otherToken.radius + 8;
          
          if (distance < minDist && distance > 0) {
            const pushDistance = (minDist - distance) * 0.5;
            const pushX = (dx / distance) * pushDistance;
            const pushY = (dy / distance) * pushDistance;
            newX += pushX;
            newY += pushY;
          }
        }
      });
      
      // Repulsion from non-selected tokens
      allTokens.forEach(otherToken => {
        if (!selectedTokenIds.includes(otherToken.id)) {
          const dx = newX - otherToken.x;
          const dy = newY - otherToken.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const minDist = token.radius + otherToken.radius + 8;
          
          if (distance < minDist && distance > 0) {
            const pushDistance = (minDist - distance) * 0.6;
            const pushX = (dx / distance) * pushDistance;
            const pushY = (dy / distance) * pushDistance;
            newX += pushX;
            newY += pushY;
          }
        }
      });
      
      updatedTokens.push({
        id: token.id,
        x: newX,
        y: newY
      });
    });
    
    return updatedTokens;
  }, []);

  // Helper function to get layer data from layer ID - MOVED UP TO FIX INITIALIZATION ERROR
  const getLayerData = useCallback((layerId) => {
    const layer = layers.find(l => l.layer_id === layerId);
    if (!layer) {
      return { lines: [] };
    }
    
    try {
      const parsed = JSON.parse(layer.layer_data || '{"lines":[]}');
      return parsed;
    } catch (err) {
      console.error(`Error parsing layer data for layer ${layerId}:`, err);
      return { lines: [] };
    }
  }, [layers]);

  // Migration function to convert existing images to containers
  const migrateExistingImagesToContainers = useCallback(async () => {
    for (const layer of layers) {
      const layerData = getLayerData(layer.layer_id);
      
      // Check if layer has an image that hasn't been migrated yet
      if (layerData.image && !layerData.migrated) {
        try {
          // Create container from existing image
          const newContainer = await addContainer({
            name: layer.layer_name || 'Migrated Image',
            layerId: layer.layer_id,
            image: {
              src: layerData.image.src,
              width: layerData.image.width,
              height: layerData.image.height
            },
            x: layerData.image.x || 0,
            y: layerData.image.y || 0,
            scaleX: 1,
            scaleY: 1,
            rotation: 0
          });
          
          if (newContainer) {
            // Mark layer as migrated and remove old image data
            const updatedLayerData = {
              ...layerData,
              migrated: true,
              image: undefined // Remove old image data
            };
            
            await updateLayer(layer.layer_id, {
              layer_data: JSON.stringify(updatedLayerData)
            });
          }
        } catch (error) {
          console.error(`[MapDisplay] Failed to migrate image in layer ${layer.layer_name}:`, error);
        }
      }
    }
  }, [layers, addContainer, updateLayer, getLayerData]);

  // Run migration on component mount and when layers change
  useEffect(() => {
    if (layers.length > 0) {
      migrateExistingImagesToContainers();
    }
  }, [layers.length, migrateExistingImagesToContainers]); // Only run when layers are first loaded

  // Container interaction handlers
  const handleContainerSelect = useCallback((containerId) => {
    setSelectedContainerId(containerId);
    // Clear image selection when selecting container
    setSelectedImageId(null);
  }, [setSelectedContainerId]);

  const handleContainerUpdate = useCallback((containerId, updates) => {
    updateContainer(containerId, updates);
  }, [updateContainer]);

  const handleContainerHoverStart = useCallback((containerId, x, y) => {
    // Show hover menu in edit mode regardless of active tool
    if (isEditMode) {
      showHoverMenu(containerId, x, y);
    }
  }, [isEditMode, showHoverMenu]);

  const handleContainerHoverEnd = useCallback(() => {
    hideHoverMenu();
  }, [hideHoverMenu]);

  const handleContainerMenuAction = useCallback((action) => {
    if (!hoveredContainerId) return;

    switch (action) {
      case 'delete':
        deleteContainer(hoveredContainerId);
        // Hide menu immediately for delete action
        hideHoverMenu();
        break;
      case 'toggleLock':
        toggleLock(hoveredContainerId);
        // Don't hide menu for lock toggle - let user continue using menu
        break;
      case 'toFront':
        bringToFront(hoveredContainerId);
        // Don't hide menu for z-order changes - let user continue using menu
        break;
      case 'toBack':
        sendToBack(hoveredContainerId);
        // Don't hide menu for z-order changes - let user continue using menu
        break;
      case 'duplicate':
        duplicateContainer(hoveredContainerId);
        // Hide menu after duplicate since a new container is created
        hideHoverMenu();
        break;
      default:
        console.warn('[MapDisplay] Unknown container menu action:', action);
        break;
    }
  }, [hoveredContainerId, deleteContainer, toggleLock, bringToFront, sendToBack, duplicateContainer, hideHoverMenu]);

  // Drawing on containers disabled: no handler

  // Update FOV with container drawings
  const updateFOVWithContainers = useCallback(() => {
    if (!useLineBasedFOV || !lineBasedFOVRef.current) return;

    try {
      // Get all wall drawings from containers in FOW layers
      const fowLayers = layers.filter(layer => layer.is_wall_layer && layer.is_visible);
      const allWalls = [];

      fowLayers.forEach(layer => {
        const layerContainers = getContainersByLayer(layer.layer_id);
        
        layerContainers.forEach(container => {
          // Ensure drawings exists and is an array before iterating
          if (container.drawings && Array.isArray(container.drawings)) {
            container.drawings.forEach(drawing => {
              if (drawing.tool === TOOLS.WALL) {
                // Convert relative coordinates to global coordinates
                const globalPoints = convertPointsToGlobal(drawing.points, container);
                allWalls.push({
                  id: `${container.id}-${drawing.id}`,
                  points: globalPoints,
                  color: drawing.color,
                  strokeWidth: drawing.strokeWidth
                });
              }
            });
          }
        });
      });

      // Update FOV system with walls from containers
      if (lineBasedFOVRef.current.updateWalls) {
        lineBasedFOVRef.current.updateWalls(allWalls);
      }

    } catch (error) {
      console.error('[MapDisplay] Error updating FOV with containers:', error);
    }
  }, [useLineBasedFOV, layers, getContainersByLayer]);

  // Update FOV when containers change
  useEffect(() => {
    updateFOVWithContainers();
  }, [containers, updateFOVWithContainers]);

  // Removed renderEditModeWallSegments function - unused debug function, debug functionality available via renderFovDebugRays()

  // Handle window resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { offsetWidth, offsetHeight } = containerRef.current;
        setStageSize({
          width: offsetWidth,
          height: offsetHeight
        });
        
        // Force stage to update if it exists
        if (stageRef.current) {
          const stage = stageRef.current;
          setTimeout(() => {
            stage.width(offsetWidth);
            stage.height(offsetHeight);
            stage.batchDraw();
          }, 0);
        }
      }
    };

    // Add handlers for window focus/blur and visibility changes
    const handleWindowFocus = () => {
      // When window regains focus, force a complete re-sync
      setTimeout(() => {
        updateDimensions();
        
        // Force stage to redraw and recalculate positions
        if (stageRef.current) {
          const stage = stageRef.current;
          
          // Store current position and scale before refresh
          const currentPosition = { x: stage.x(), y: stage.y() };
          const currentScale = { x: stage.scaleX(), y: stage.scaleY() };
          
          // Force a complete stage refresh
          stage.batchDraw();
          
          // Ensure all layers are properly positioned
          stage.getLayers().forEach(layer => {
            layer.clearCache();
            layer.batchDraw();
          });
          
          // Restore the exact position and scale that were active before focus loss
          stage.position(currentPosition);
          stage.scale(currentScale);
          
          // Also update our state to match the restored values
          setStagePosition(currentPosition);
          setStageScale(currentScale.x);
          
          // Recalculate container positions if any
          if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            // Ensure stage matches container exactly
            if (Math.abs(stage.width() - rect.width) > 1 || Math.abs(stage.height() - rect.height) > 1) {
              stage.width(rect.width);
              stage.height(rect.height);
              stage.batchDraw();
            }
            
            // Also ensure the stage is properly positioned within its container
            const canvas = stage.getContent();
            if (canvas && canvas.style) {
              canvas.style.position = 'absolute';
              canvas.style.top = '0px';
              canvas.style.left = '0px';
            }
          }
        }
        
        // Smart FOV recalculation if in play mode - only if actually needed
        if (!isEditMode && fowVisible && activeTokenRef.current) {
          // Check if FOV actually needs recalculation (position/scale changed meaningfully)
          const token = activeTokenRef.current;
          const lastPos = lastValidPositionRef.current;
          const positionChanged = Math.abs(token.x - lastPos.x) > 2 || Math.abs(token.y - lastPos.y) > 2;
          
          // Only trigger if position changed or if this is the first calculation
          if (positionChanged || visiblePointsRef.current.length === 0) {
            setFowRenderTrigger(prev => prev + 1);
            // Update position cache to prevent unnecessary future calculations
            lastValidPositionRef.current = { x: token.x, y: token.y };
          }
        }
        
        // Reset any drawing states that might be stuck
        if (isDrawing) {
          setIsDrawing(false);
          setCurrentLine(null);
          setWallPoints([]);
        }
      }, 50); // Small delay to ensure browser has fully rendered
    };

    const handleWindowBlur = () => {
      // Store current position and scale when losing focus
      if (stageRef.current) {
        const stage = stageRef.current;
        const currentPosition = { x: stage.x(), y: stage.y() };
        const currentScale = stage.scaleX();
        
        // Store in state for later restoration
        setStagePosition(currentPosition);
        setStageScale(currentScale);
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page became hidden - store current state
        handleWindowBlur();
      } else {
        // Page became visible again
        handleWindowFocus();
      }
    };

    window.addEventListener('resize', updateDimensions);
    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('visibilitychange', handleVisibilityChange);
    
    updateDimensions();

    return () => {
      window.removeEventListener('resize', updateDimensions);
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fowVisible, isDrawing, isEditMode]); // Added missing dependencies
  
  // Removed fog of war toggle - fog controls removed

  // Line intersection helper
  const lineIntersection = useCallback((x1, y1, x2, y2, x3, y3, x4, y4) => {
    // Check for zero-length segments
    if ((x1 === x2 && y1 === y2) || (x3 === x4 && y3 === y4)) {
      return null;
    }
    
    const denominator = ((y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1));
    
    // Check for parallel lines
    if (Math.abs(denominator) < 1e-6) {
      return null;
    }
    
    const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator;
    const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator;
    
    // Check if intersection is on both line segments
    if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
      return null;
    }
    
    // Calculate intersection point
    const x = x1 + ua * (x2 - x1);
    const y = y1 + ua * (y2 - y1);
    
    return { x, y };
  }, []);
  
  // Helper function to get points along a line using Bresenham's algorithm
  const getLinePoints = useCallback((x1, y1, x2, y2) => {
    const points = [];
    
    let x = x1;
    let y = y1;
    const dx = Math.abs(x2 - x1);
    const dy = Math.abs(y2 - y1);
    const sx = x1 < x2 ? 1 : -1;
    const sy = y1 < y2 ? 1 : -1;
    let err = dx - dy;
    
    while (true) {
      points.push(`${x},${y}`);
      
      if (x === x2 && y === y2) break;
      
      const e2 = 2 * err;
      if (e2 > -dy) {
        err -= dy;
        x += sx;
      }
      if (e2 < dx) {
        err += dx;
        y += sy;
      }
    }
    
    return points;
  }, []);
  
  // Helper function to mark wall path in grid
  const markWallPath = useCallback((x1, y1, x2, y2, gridSize, wallGrid, isAdding) => {
    // Get all points along the wall using Bresenham's algorithm
    const startX = Math.floor(x1 / gridSize);
    const startY = Math.floor(y1 / gridSize);
    const endX = Math.floor(x2 / gridSize);
    const endY = Math.floor(y2 / gridSize);
    
    const points = getLinePoints(startX, startY, endX, endY);
    
    // Add or remove these points from the wallGrid
    points.forEach(point => {
      if (isAdding) {
        wallGrid.add(point);
      } else {
        wallGrid.delete(point);
      }
    });
  }, [getLinePoints]);
  
  // Function to remove walls in eraser path
  const markEraserPathRemoveWalls = useCallback((x1, y1, x2, y2, thickness, gridSize, wallGrid) => {
    // Get the path of the eraser
    const startX = Math.floor(x1 / gridSize);
    const startY = Math.floor(y1 / gridSize);
    const endX = Math.floor(x2 / gridSize);
    const endY = Math.floor(y2 / gridSize);
    
    const linePoints = getLinePoints(startX, startY, endX, endY);
    
    // Calculate the eraser radius in grid units (thickness is diameter)
    const radiusInGrid = Math.ceil((thickness / 2) / gridSize);
    
    // For each point along the central line, remove wall cells in a circle
    linePoints.forEach(point => {
      const [x, y] = point.split(',').map(Number);
      
      // Check all cells in a square area around the point
      for (let dx = -radiusInGrid; dx <= radiusInGrid; dx++) {
        for (let dy = -radiusInGrid; dy <= radiusInGrid; dy++) {
          // Approximate a circle by checking distance
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance <= radiusInGrid) {
            // Remove this point from the wall grid
            wallGrid.delete(`${x + dx},${y + dy}`);
          }
        }
      }
    });
  }, [getLinePoints]);
  
  // Extract wall segments - memoized to prevent recreation
  const extractWalls = useCallback(() => {
    if (useLineBasedFOV && lineBasedFOVRef.current) {
      lineBasedFOVRef.current.updateWallsFromLayers(layers);
      const walls = lineBasedFOVRef.current.getSegmentCollection().segments.map(segment => ({
        x1: segment.x1, y1: segment.y1, x2: segment.x2, y2: segment.y2
      }));
      const bufferAmount = 5000;
      const mapWidth = mapImage ? mapImage.width : stageSize.width / stageScale;
      const mapHeight = mapImage ? mapImage.height : stageSize.height / stageScale;
      walls.push({ x1: -bufferAmount, y1: -bufferAmount, x2: mapWidth + bufferAmount, y2: -bufferAmount });
      walls.push({ x1: mapWidth + bufferAmount, y1: -bufferAmount, x2: mapWidth + bufferAmount, y2: mapHeight + bufferAmount });
      walls.push({ x1: mapWidth + bufferAmount, y1: mapHeight + bufferAmount, x2: -bufferAmount, y2: mapHeight + bufferAmount });
      walls.push({ x1: -bufferAmount, y1: mapHeight + bufferAmount, x2: -bufferAmount, y2: -bufferAmount });
      wallsRef.current = walls;
      losObjectsRef.current = [];
      return;
    }
    
    // קוד מערכת ישנה
    const walls = [];
    const losObjects = []; // Initialize list for LoS objects
    
    // Get all layers in order from bottom (oldest) to top (newest)
    const orderedLayers = [...layers].sort((a, b) => a.layer_order - b.layer_order);
    
    // Track operations by their timestamp (layer order + line position)
    // Higher timestamps mean newer operations that should take precedence
    const eraserOperations = []; // [{x1, y1, x2, y2, thickness, timestamp}]
    const wallOperations = [];   // [{x1, y1, x2, y2, timestamp}]
    
    // First pass - collect all operations with their timestamps
    orderedLayers.forEach((layer, layerIndex) => {
      if (layer.is_visible !== false) { // Only process visible layers
        try {
          const layerData = JSON.parse(layer.layer_data || '{"lines":[]}');
          const layerLines = layerData.lines || [];
          
          layerLines.forEach((line, lineIndex) => {
            const timestamp = layerIndex * 10000 + lineIndex;

            // First, handle erasers regardless of wall layer status
            if (line.tool === TOOLS.ERASER && line.points && line.points.length >= 4) {
              for (let i = 0; i < line.points.length - 3; i += 2) {
                eraserOperations.push({
                  x1: line.points[i], y1: line.points[i + 1],
                  x2: line.points[i + 2], y2: line.points[i + 3],
                  thickness: line.strokeWidth || 20,
                  timestamp
                });
              }
              return; // Continue to next line after processing eraser
            }

            // If the layer is NOT a wall layer, skip all other LoS processing for this line
            if (!layer.is_wall_layer) {
              return; // Continue to next line, effectively ignoring it for LoS
            }

            // --- From this point, layer.is_wall_layer is true ---

            // Standard walls (Pencil or Wall tool on a wall layer)
            if ((line.tool === TOOLS.WALL || line.tool === TOOLS.PENCIL) && line.points && line.points.length >= 4) {
              for (let i = 0; i < line.points.length - 3; i += 2) {
                wallOperations.push({
                  x1: line.points[i], y1: line.points[i + 1],
                  x2: line.points[i + 2], y2: line.points[i + 3],
                  timestamp,
                  wallType: 'line'
                });
              }
            }
            // Shapes on a wall layer
            else if (line.tool === TOOLS.POLYGON || line.tool === TOOLS.RECTANGLE || line.tool === TOOLS.CIRCLE) {
              if (line.isLOSObject) { // twoHitLOS mode (Opaque button OFF)
                if (line.tool === TOOLS.POLYGON && line.points && line.points.length >= 6) {
                  losObjects.push({ ...line, shapeType: TOOLS.POLYGON });
                } else if (line.tool === TOOLS.RECTANGLE && line.width && line.height) {
                  losObjects.push({ ...line, shapeType: TOOLS.RECTANGLE });
                } else if (line.tool === TOOLS.CIRCLE && line.radius) {
                  losObjects.push({ ...line, shapeType: TOOLS.CIRCLE });
                }
              } else { // standardWall mode for shapes (Opaque button ON) or if not an LOS object
                let segments = [];
                if (line.tool === TOOLS.POLYGON && line.points && line.points.length >= 4) {
                  for (let i = 0; i < line.points.length - 2; i += 2) {
                    segments.push({ 
                      x1: line.points[i], 
                      y1: line.points[i+1], 
                      x2: line.points[i+2], 
                      y2: line.points[i+3],
                      wallType: 'polygon-segment', 
                      shapeId: line.id || `poly-${timestamp}`
                    });
                  }
                  // Ensure polygon is closed by adding a segment from last point to first point
                  if (line.points.length >= 6) { // Need at least 3 points to close
                    segments.push({
                        x1: line.points[line.points.length - 2],
                        y1: line.points[line.points.length - 1],
                        x2: line.points[0],
                        y2: line.points[1],
                        wallType: 'polygon-segment',
                        shapeId: line.id || `poly-${timestamp}`
                    });
                  }
                } else if (line.tool === TOOLS.RECTANGLE && line.width && line.height) {
                  const { x, y, width, height } = line;
                  const rectId = line.id || `rect-${timestamp}`;
                  segments.push({ x1: x, y1: y, x2: x + width, y2: y, wallType: 'rectangle-segment', shapeId: rectId });
                  segments.push({ x1: x + width, y1: y, x2: x + width, y2: y + height, wallType: 'rectangle-segment', shapeId: rectId });
                  segments.push({ x1: x + width, y1: y + height, x2: x, y2: y + height, wallType: 'rectangle-segment', shapeId: rectId });
                  segments.push({ x1: x, y1: y + height, x2: x, y2: y, wallType: 'rectangle-segment', shapeId: rectId });
                } else if (line.tool === TOOLS.CIRCLE && line.radius) {
                  const { x: cx, y: cy, radius } = line;
                  const numSegments = Math.max(12, Math.floor(radius / 5)); // Ensure enough segments for a circle
                  let prevX = cx + radius; let prevY = cy;
                  const circleId = line.id || `circle-${timestamp}`;
                  for (let i = 1; i <= numSegments; i++) {
                    const angle = (i / numSegments) * 2 * Math.PI;
                    const nextX = cx + radius * Math.cos(angle);
                    const nextY = cy + radius * Math.sin(angle);
                    segments.push({ 
                      x1: prevX, 
                      y1: prevY, 
                      x2: nextX, 
                      y2: nextY,
                      wallType: 'circle-segment',
                      shapeId: circleId
                    });
                    prevX = nextX; prevY = nextY;
                  }
                }
                for (const seg of segments) {
                  wallOperations.push({ ...seg, timestamp });
                }
              }
            }
          });
        } catch (err) {
          console.error('Error processing layer data:', err);
        }
      }
    });
    
    // Sort operations by timestamp to process them in chronological order
    const allOperations = [...eraserOperations, ...wallOperations]
      .sort((a, b) => a.timestamp - b.timestamp);
    
    // Track which grid cells currently have walls
    const gridSize = 5; // Smaller grid size for more precision
    const wallGrid = new Set(); // Set of grid cells that have walls
    const shapeMask = new Map(); // Track which shapes have been affected and where
    
    // Process operations in chronological order
    allOperations.forEach(op => {
      if ('thickness' in op) {
        // This is an eraser operation
        markEraserPathRemoveWalls(op.x1, op.y1, op.x2, op.y2, op.thickness, gridSize, wallGrid);
      } else {
        // This is a wall operation - mark points along the wall
        markWallPath(op.x1, op.y1, op.x2, op.y2, gridSize, wallGrid, true); // true = add wall
        
        // If this is a shape segment, track it for more accurate shape masking
        if (op.wallType && op.wallType !== 'line' && op.shapeId) {
          if (!shapeMask.has(op.shapeId)) {
            shapeMask.set(op.shapeId, new Set());
          }
          
          // Add all points of this segment to the shape's mask
          const points = getLinePoints(
            Math.floor(op.x1 / gridSize), Math.floor(op.y1 / gridSize),
            Math.floor(op.x2 / gridSize), Math.floor(op.y2 / gridSize)
          );
          
          const shapeMaskSet = shapeMask.get(op.shapeId);
          points.forEach(point => shapeMaskSet.add(point));
        }
      }
    });
    
    // Now convert our wallGrid back to wall segments for FOV calculation
    // For each wall operation, check if its points are in the wallGrid
    wallOperations.forEach(wall => {
      // Get points along the wall line
      const wallPoints = getLinePoints(
        Math.floor(wall.x1 / gridSize), Math.floor(wall.y1 / gridSize),
        Math.floor(wall.x2 / gridSize), Math.floor(wall.y2 / gridSize)
      );
      
      // For standard walls, check if the majority of points are still in the wallGrid
      if (wall.wallType === 'line' || !wall.shapeId) {
      let pointsInGrid = 0;
      for (const point of wallPoints) {
        if (wallGrid.has(point)) {
          pointsInGrid++;
        }
      }
      
      // If at least half the points are still in the grid, include this wall
      if (pointsInGrid >= wallPoints.length * 0.5) {
        walls.push({
          x1: wall.x1, y1: wall.y1, x2: wall.x2, y2: wall.y2
        });
        }
      }
      // For shape segments, be more precise about the segments we include
      else if (wall.shapeId && shapeMask.has(wall.shapeId)) {
        let pointsInGrid = 0;
        let totalPoints = 0;
        
        for (const point of wallPoints) {
          totalPoints++;
          // Check if this point is both in the shape's mask AND in the wall grid
          if (shapeMask.get(wall.shapeId).has(point) && wallGrid.has(point)) {
            pointsInGrid++;
          }
        }
        
        // We use a slightly lower threshold for shape segments to be more sensitive
        if (pointsInGrid >= totalPoints * 0.3) {
          walls.push({
            x1: wall.x1, y1: wall.y1, x2: wall.x2, y2: wall.y2
          });
        }
      }
    });
    
    // Add map boundaries with a large buffer to prevent edge visibility
    const bufferAmount = 5000; // Large buffer to ensure coverage even when zoomed out
    const mapWidth = mapImage ? mapImage.width : stageSize.width / stageScale;
    const mapHeight = mapImage ? mapImage.height : stageSize.height / stageScale;
    
    // Extended boundaries to ensure no gaps at edges
    walls.push({ x1: -bufferAmount, y1: -bufferAmount, x2: mapWidth + bufferAmount, y2: -bufferAmount }); // Top
    walls.push({ x1: mapWidth + bufferAmount, y1: -bufferAmount, x2: mapWidth + bufferAmount, y2: mapHeight + bufferAmount }); // Right
    walls.push({ x1: mapWidth + bufferAmount, y1: mapHeight + bufferAmount, x2: -bufferAmount, y2: mapHeight + bufferAmount }); // Bottom
    walls.push({ x1: -bufferAmount, y1: mapHeight + bufferAmount, x2: -bufferAmount, y2: -bufferAmount }); // Left
    
    wallsRef.current = walls;
    losObjectsRef.current = losObjects; // Store the extracted LoS objects
  }, [useLineBasedFOV, lineBasedFOVRef, layers, mapImage, stageSize.width, stageSize.height, stageScale, markEraserPathRemoveWalls, markWallPath, getLinePoints]);
  
  // Helper function for Ray-Circle Intersection (MOVED TO BEFORE calculateVisibility)
  const rayCircleIntersectionPoints = useCallback((rayOriginX, rayOriginY, rayDirX, rayDirY, circleX, circleY, radius) => {
    const ocX = rayOriginX - circleX;
    const ocY = rayOriginY - circleY;

    const a = rayDirX * rayDirX + rayDirY * rayDirY; // Should be 1 if rayDir is normalized, but robust this way
    const b = 2 * (ocX * rayDirX + ocY * rayDirY);
    const c = ocX * ocX + ocY * ocY - radius * radius;

    const discriminant = b * b - 4 * a * c;

    if (discriminant < 0) {
      return []; // No intersection
    }

    const t1 = (-b - Math.sqrt(discriminant)) / (2 * a);
    const t2 = (-b + Math.sqrt(discriminant)) / (2 * a);

    const intersections = [];
    if (t1 >= 0) { // Intersection point is along the ray's forward direction
      intersections.push({ 
        x: rayOriginX + t1 * rayDirX,
        y: rayOriginY + t1 * rayDirY,
        t: t1
      });
    }
    if (discriminant > 0 && t2 >= 0 && t1 !== t2) { // Second distinct intersection point
       intersections.push({ 
        x: rayOriginX + t2 * rayDirX,
        y: rayOriginY + t2 * rayDirY,
        t: t2
      });
    }
    
    // Sort by distance t, though t1 should always be <= t2 with this formula
    return intersections.sort((p1, p2) => p1.t - p2.t);
  }, []);
  
  // Calculate visibility using raycasting
  const calculateVisibility = useCallback(() => {
    if (!activeTokenRef.current) return [];
    
    // For group lighting mode, each token's visibility is calculated separately in the fogOfWarMask
    // This function now only handles the single active token for consistency
    if (useLineBasedFOV && lineBasedFOVRef.current && activeTokenRef.current) {
      const token = activeTokenRef.current;
      const visionRadius = token.visionRadius || 200;
      return lineBasedFOVRef.current.calculateVisibility(token.x, token.y, visionRadius);
    }
    // להלן הקוד של המערכת הישנה
    const token = activeTokenRef.current;
    const visionRadius = token.visionRadius || 200;
    const standardWalls = wallsRef.current; // Normal walls
    const specialLosObjects = losObjectsRef.current; // Objects with 2-hit rule
    const visibilityPolygonPoints = [];

    const numRays = 720; // Higher ray count for smooth edges

    for (let i = 0; i < numRays; i++) {
      const angle = (i / numRays) * Math.PI * 2;
      const rayDirX = Math.cos(angle);
      const rayDirY = Math.sin(angle);

      // Extended ray endpoint for intersection checks beyond vision radius
      const rayEndX = token.x + rayDirX * visionRadius * 1.5;
      const rayEndY = token.y + rayDirY * visionRadius * 1.5;

      let allIntersections = [];

      // 1. Check intersections with standard walls
      for (const wall of standardWalls) {
        const intersection = lineIntersection(
          token.x, token.y, rayEndX, rayEndY, // Ray segment
          wall.x1, wall.y1, wall.x2, wall.y2  // Wall segment
        );
        if (intersection) {
          const dx = intersection.x - token.x;
          const dy = intersection.y - token.y;
          allIntersections.push({
            x: intersection.x, y: intersection.y,
            distSq: dx * dx + dy * dy,
            objectType: 'wall',
            isBlocking: true // Standard walls always block immediately
          });
        }
      }

      // 2. Check intersections with special LoS objects
      for (const losObj of specialLosObjects) {
        if (losObj.shapeType === TOOLS.POLYGON) {
          for (let j = 0; j < losObj.points.length - 2; j += 2) {
            const p1x = losObj.points[j];
            const p1y = losObj.points[j + 1];
            const p2x = losObj.points[j + 2];
            const p2y = losObj.points[j + 3];
            const intersection = lineIntersection(token.x, token.y, rayEndX, rayEndY, p1x, p1y, p2x, p2y);
            if (intersection) {
              const dx = intersection.x - token.x;
              const dy = intersection.y - token.y;
              allIntersections.push({
                x: intersection.x, y: intersection.y,
                distSq: dx * dx + dy * dy,
                objectType: 'los',
                objectRef: losObj, // Reference to the specific object
              });
            }
          }
        } else if (losObj.shapeType === TOOLS.RECTANGLE) {
          const { x: rx, y: ry, width: rw, height: rh } = losObj;
          const rectPoints = [
            rx, ry, rx + rw, ry, // Top
            rx + rw, ry, rx + rw, ry + rh, // Right
            rx + rw, ry + rh, rx, ry + rh, // Bottom
            rx, ry + rh, rx, ry // Left
          ];
          for (let j = 0; j < rectPoints.length; j += 4) {
            const intersection = lineIntersection(token.x, token.y, rayEndX, rayEndY, rectPoints[j], rectPoints[j+1], rectPoints[j+2], rectPoints[j+3]);
            if (intersection) {
              const dx = intersection.x - token.x;
              const dy = intersection.y - token.y;
              allIntersections.push({
                x: intersection.x, y: intersection.y,
                distSq: dx * dx + dy * dy,
                objectType: 'los',
                objectRef: losObj,
              });
            }
          }
        } else if (losObj.shapeType === TOOLS.CIRCLE) {
          const circleIntersections = rayCircleIntersectionPoints(token.x, token.y, rayDirX, rayDirY, losObj.x, losObj.y, losObj.radius);
          for (const intersect of circleIntersections) {
            // Ensure intersection is within the ray's extended segment length although rayCircleIntersectionPoints t >= 0 handles forward direction
            const dx = intersect.x - token.x;
            const dy = intersect.y - token.y;
            const distSq = dx*dx + dy*dy;
            if (distSq <= (visionRadius * 1.5) * (visionRadius * 1.5)) { // Check within extended ray length
                allIntersections.push({
                    x: intersect.x, y: intersect.y,
                    distSq: distSq,
                    objectType: 'los',
                    objectRef: losObj,
                });
            }
          }
        }
      }

      // 3. Sort all intersections by distance
      allIntersections.sort((a, b) => a.distSq - b.distSq);

      // 4. Determine the actual blocking point for this ray
      let finalRayEndX = token.x + rayDirX * visionRadius;
      let finalRayEndY = token.y + rayDirY * visionRadius;
      // let rayBlocked = false; // Commented out - debug variable that tracks blocking state but never used in logic

      const losHitCounts = new Map(); // Track hits per LoS object for this ray: Map<objectRef, count>

      for (const intersection of allIntersections) {
        if (intersection.distSq > visionRadius * visionRadius) {
          // Intersection is beyond vision radius, ignore it and subsequent ones for blocking
          break; 
        }

        if (intersection.objectType === 'wall') {
          finalRayEndX = intersection.x;
          finalRayEndY = intersection.y;
          // rayBlocked = true; // Commented out - debug variable not used
          break; // Wall blocks immediately
        } else if (intersection.objectType === 'los') {
          const currentHits = (losHitCounts.get(intersection.objectRef) || 0) + 1;
          losHitCounts.set(intersection.objectRef, currentHits);

          if (currentHits >= 2) {
            finalRayEndX = intersection.x;
            finalRayEndY = intersection.y;
            // rayBlocked = true; // Commented out - debug variable not used
            break; // LoS object blocks on second hit
          }
          // If first hit on an LoS object, ray passes through, continue checking other intersections closer to the token
        }
      }
      
      visibilityPolygonPoints.push(finalRayEndX, finalRayEndY);
    }

    // Close the polygon
    if (visibilityPolygonPoints.length >= 2) {
      visibilityPolygonPoints.push(visibilityPolygonPoints[0]);
      visibilityPolygonPoints.push(visibilityPolygonPoints[1]);
    }
    
    return visibilityPolygonPoints;
  }, [useLineBasedFOV, lineBasedFOVRef, activeTokenRef, lineIntersection, rayCircleIntersectionPoints]);

  // Store initial positions for undo/redo
  const initialDragPositions = useRef({});

  // NEW: Handler for when a token drag actually starts
  const handleTokenActualDragStart = useCallback((id) => {
    try {
      draggingTokenIdRef.current = id;
      setCurrentlyDraggedTokenId(id); // Update state for useEffect dependencies
      // setIsDragging(true); // Removed - isDragging state no longer tracked
      
      // Immediately update the active token to the one being dragged (for FOV calculations)
      const draggedToken = propTokens.find(t => t.id === id);
      if (draggedToken && (!isEditMode && fowVisible)) {
        activeTokenRef.current = draggedToken;
        tokenPositionRef.current = { x: draggedToken.x, y: draggedToken.y };
        
        // Force immediate FOV recalculation with the new active token
        if (extractWallsRef.current) {
          extractWallsRef.current();
        }
        if (calculateVisibilityRef.current) {
          visiblePointsRef.current = calculateVisibilityRef.current();
        }
        lastUpdateTimeRef.current = Date.now();
        setFowRenderTrigger(prev => prev + 1);
      }
      
      // Store initial positions for undo/redo (before any changes)
      const tokensToMove = selectedTokenIds.includes(id) && selectedTokenIds.length > 1 
        ? selectedTokenIds 
        : [id];
      
      const positions = {};
      tokensToMove.forEach(tokenId => {
        const token = propTokens.find(t => t.id === tokenId);
        if (token) {
          positions[tokenId] = { x: token.x, y: token.y };
        }
      });
      
      initialDragPositions.current = positions;
      
      // If this token is part of a multi-selection, prepare for multi-drag
      if (selectedTokenIds.includes(id) && selectedTokenIds.length > 1) {
        const draggedToken = propTokens.find(t => t.id === id);
        if (draggedToken) {
          // Store initial positions of all selected tokens
          setDragStartPositions(positions);
          // setDragOffset({ x: 0, y: 0 }); // Removed - using local offset values
        }
      }
      
      // Preemptively update viewport bounds when starting to drag the active token
      if (!isEditMode && fowVisible && activeTokenRef.current && activeTokenRef.current.id === id) {
        if (useLineBasedFOV && lineBasedFOVRef.current && stageRef.current) {
          const stage = stageRef.current;
          const token = activeTokenRef.current;
          const visionRadius = token.visionRadius || 200;
          
          // Calculate current viewport bounds
          const currentViewportBounds = {
            x: -stage.x() / stage.scaleX(),
            y: -stage.y() / stage.scaleY(),
            width: stageSize.width / stage.scaleX(),
            height: stageSize.height / stage.scaleY()
          };
          
          // Preemptively expand viewport bounds to include token's potential movement area
          const tokenMinX = token.x - visionRadius;
          const tokenMinY = token.y - visionRadius;
          const tokenMaxX = token.x + visionRadius;
          const tokenMaxY = token.y + visionRadius;
          
          const expandedBounds = {
            x: Math.min(currentViewportBounds.x, tokenMinX),
            y: Math.min(currentViewportBounds.y, tokenMinY),
            width: Math.max(currentViewportBounds.x + currentViewportBounds.width, tokenMaxX) - Math.min(currentViewportBounds.x, tokenMinX),
            height: Math.max(currentViewportBounds.y + currentViewportBounds.height, tokenMaxY) - Math.min(currentViewportBounds.y, tokenMinY)
          };
          
          // Validate and set expanded bounds
          if (isFinite(expandedBounds.x) && isFinite(expandedBounds.y) &&
              isFinite(expandedBounds.width) && isFinite(expandedBounds.height) &&
              expandedBounds.width > 0 && expandedBounds.height > 0) {
            lineBasedFOVRef.current.setViewportBounds(expandedBounds);
            expandedViewportBoundsRef.current = expandedBounds;
            
            // Clear cache to ensure fresh calculation with expanded bounds
            if (lineBasedFOVRef.current.getFOV && lineBasedFOVRef.current.getFOV().clearCache) {
              lineBasedFOVRef.current.getFOV().clearCache();
            }
          }
        }
      }
    } catch (err) {
      console.error('Error in handleTokenActualDragStart:', err);
    }
  }, [selectedTokenIds, propTokens, isEditMode, fowVisible, useLineBasedFOV, stageSize]); // Removed extractWalls and calculateVisibility

  // Throttle FOV update during token drag
  const throttledFOVTimeout = useRef(null);
  
  // Cache for expanded viewport bounds to avoid repeated calculations
  const expandedViewportBoundsRef = useRef(null);
  const lastViewportUpdateTime = useRef(0);
  
  // Cache for mouse position to prevent redundant updates
  const lastMousePositionRef = useRef({ x: 0, y: 0 });
  const lastDragUpdateTimeRef = useRef(0);

  // Handle token drag - update token position with live multi-token dragging
  const handleTokenDragMove = useCallback((id, x, y) => {
    try {
      // Only process if it's one of the selected tokens AND it's the token we know is being dragged
      if (selectedTokenIds.includes(id) && draggingTokenIdRef.current === id) {
        // Quick optimization: Skip if position hasn't changed significantly
        const lastPos = lastMousePositionRef.current;
        const positionDelta = Math.abs(x - lastPos.x) + Math.abs(y - lastPos.y);
        const now = Date.now();
        
        // Skip update if movement is too small (< 2 pixels) and we updated recently (< 16ms = 60fps)
        if (positionDelta < 2 && now - lastDragUpdateTimeRef.current < 16) {
          return;
        }
        
        lastMousePositionRef.current = { x, y };
        lastDragUpdateTimeRef.current = now;
        // 1. IMMEDIATE VISUAL UPDATE - Fast path for responsive UI
        if (selectedTokenIds.length > 1 && dragStartPositions[id]) {
          const newOffset = {
            x: x - dragStartPositions[id].x,
            y: y - dragStartPositions[id].y
          };
          // setDragOffset(newOffset); // Removed - using local newOffset value instead
          
          // Live update: Calculate new positions for all selected tokens with collision avoidance
          const updatedTokens = calculateTokenPositionsWithCollisionAvoidance(
            selectedTokenIds, 
            dragStartPositions, 
            newOffset, 
            propTokens,
            { x, y }, // Mouse position
            id // The token being dragged
          );
          
          // Update all selected tokens with their new positions IMMEDIATELY
          updatedTokens.forEach(tokenUpdate => {
            if (onUpdateToken) {
              onUpdateToken(tokenUpdate.id, { x: tokenUpdate.x, y: tokenUpdate.y });
            }
          });
        } else {
          // Single token drag - update immediately
          if (onUpdateToken) {
            onUpdateToken(id, { x, y });
          }
        }
        
        // 2. UPDATE ACTIVE TOKEN REFERENCE for FOV (but don't calculate yet)
        if (!isEditMode && fowVisible && activeTokenRef.current && activeTokenRef.current.id === id) {
          activeTokenRef.current = { ...activeTokenRef.current, x, y };
          tokenPositionRef.current = { x, y };
          
          // Update position cache for RAF efficiency
          lastValidPositionRef.current = { x, y };
        }
        
        // 3. OPTIMIZED FOV CALCULATION - Heavy operations with smart throttling
        if (!isEditMode && fowVisible) {
          // Clear any existing FOV timeout
          if (throttledFOVTimeout.current) {
            clearTimeout(throttledFOVTimeout.current);
            throttledFOVTimeout.current = null;
          }
          
          // Schedule FOV calculation with longer delay for better performance
          throttledFOVTimeout.current = setTimeout(() => {
            // Update viewport bounds only when needed
            if (useLineBasedFOV && lineBasedFOVRef.current && stageRef.current && activeTokenRef.current) {
              const now = Date.now();
              // Less frequent viewport updates - only every 200ms instead of 100ms
              if (now - lastViewportUpdateTime.current > 200) {
                const stage = stageRef.current;
                const token = activeTokenRef.current;
                const visionRadius = token.visionRadius || 200;
                
                // Calculate current viewport bounds (optimized - fewer calculations)
                const stageX = stage.x();
                const stageY = stage.y();
                const stageScaleX = stage.scaleX();
                const stageScaleY = stage.scaleY();
                
                const currentViewportBounds = {
                  x: -stageX / stageScaleX,
                  y: -stageY / stageScaleY,
                  width: stageSize.width / stageScaleX,
                  height: stageSize.height / stageScaleY
                };
                
                // Quick bounds validation (faster than multiple isFinite calls)
                const boundsValid = currentViewportBounds.width > 0 && 
                                  currentViewportBounds.height > 0 &&
                                  !isNaN(currentViewportBounds.x) &&
                                  !isNaN(currentViewportBounds.y);
                
                if (boundsValid) {
                  const tokenMinX = token.x - visionRadius;
                  const tokenMinY = token.y - visionRadius;
                  const tokenMaxX = token.x + visionRadius;
                  const tokenMaxY = token.y + visionRadius;
                  
                  const currentMaxX = currentViewportBounds.x + currentViewportBounds.width;
                  const currentMaxY = currentViewportBounds.y + currentViewportBounds.height;
                  
                  const needsExpansion = tokenMinX < currentViewportBounds.x || 
                                       tokenMinY < currentViewportBounds.y ||
                                       tokenMaxX > currentMaxX || 
                                       tokenMaxY > currentMaxY;
                  
                  if (needsExpansion) {
                    const expandedBounds = {
                      x: Math.min(currentViewportBounds.x, tokenMinX),
                      y: Math.min(currentViewportBounds.y, tokenMinY),
                      width: Math.max(currentMaxX, tokenMaxX) - Math.min(currentViewportBounds.x, tokenMinX),
                      height: Math.max(currentMaxY, tokenMaxY) - Math.min(currentViewportBounds.y, tokenMinY)
                    };
                    
                    // Check if bounds actually changed significantly (larger threshold)
                    const prevBounds = expandedViewportBoundsRef.current;
                    const boundsChanged = !prevBounds || 
                      Math.abs(expandedBounds.x - prevBounds.x) > 20 ||
                      Math.abs(expandedBounds.y - prevBounds.y) > 20 ||
                      Math.abs(expandedBounds.width - prevBounds.width) > 20 ||
                      Math.abs(expandedBounds.height - prevBounds.height) > 20;
                    
                    if (boundsChanged && expandedBounds.width > 0 && expandedBounds.height > 0) {
                      lineBasedFOVRef.current.setViewportBounds(expandedBounds);
                      expandedViewportBoundsRef.current = expandedBounds;
                      lastViewportUpdateTime.current = now;
                      
                      // Clear cache only when bounds actually change significantly
                      if (lineBasedFOVRef.current.getFOV && lineBasedFOVRef.current.getFOV().clearCache) {
                        lineBasedFOVRef.current.getFOV().clearCache();
                      }
                    }
                  }
                }
              }
            }
            
            // Calculate visibility and update render
            if (calculateVisibilityRef.current) {
              visiblePointsRef.current = calculateVisibilityRef.current();
            }
            setFowRenderTrigger(prev => prev + 1);
            throttledFOVTimeout.current = null;
          }, 100); // Increased from 50ms to 100ms for better performance
        }
      }
    } catch (err) {
      console.error('Error in handleTokenDragMove:', err);
    }
  }, [selectedTokenIds, dragStartPositions, onUpdateToken, propTokens, calculateTokenPositionsWithCollisionAvoidance, isEditMode, fowVisible, useLineBasedFOV, stageSize]); // Removed calculateVisibility dependency

  // Handle token drag end - simplified since we update live now
  const handleTokenDragEnd = useCallback((id, x, y) => {
    try {
      // Create undo/redo command for the move
      if (Object.keys(initialDragPositions.current).length > 0) {
        // Get current positions after drag
        const finalPositions = {};
        Object.keys(initialDragPositions.current).forEach(tokenId => {
          const token = propTokens.find(t => t.id === tokenId);
          if (token) {
            finalPositions[tokenId] = { x: token.x, y: token.y };
          }
        });
        
        // Only create command if positions actually changed
        const hasChanged = Object.keys(initialDragPositions.current).some(tokenId => {
          const initial = initialDragPositions.current[tokenId];
          const final = finalPositions[tokenId];
          return initial && final && (Math.abs(initial.x - final.x) > 1 || Math.abs(initial.y - final.y) > 1);
        });
        
        if (hasChanged) {
          pushCommand({
            type: 'MOVE_TOKEN',
            previousStates: Object.keys(initialDragPositions.current).map(tokenId => ({
              id: tokenId,
              ...initialDragPositions.current[tokenId]
            })),
            newStates: Object.keys(initialDragPositions.current).map(tokenId => ({
              id: tokenId,
              ...finalPositions[tokenId]
            })),
            selectedTokenIds: Object.keys(initialDragPositions.current)
          });
        }
      }
      
      // Store the dragged token as the last dragged token (to keep it as active)
      if (draggingTokenIdRef.current) {
        setLastDraggedTokenId(draggingTokenIdRef.current);
      }
      
      // Clear the dragging token ID
      draggingTokenIdRef.current = null; 
      setCurrentlyDraggedTokenId(null); // Clear state for useEffect dependencies
      
      // Clean up drag state (tokens should already be in correct positions from live updates)
      setDragStartPositions({});
      // setDragOffset({ x: 0, y: 0 }); // Removed - no longer tracking global offset
      // setIsDragging(false); // Removed - isDragging state no longer tracked
      initialDragPositions.current = {};
      
      // Clear viewport bounds cache
      expandedViewportBoundsRef.current = null;
      lastViewportUpdateTime.current = 0;
    } catch (err) {
      console.error('Error in handleTokenDragEnd:', err);
    }
    // Reset viewport bounds to normal after drag ends
    if (useLineBasedFOV && lineBasedFOVRef.current && stageRef.current) {
      const stage = stageRef.current;
      const normalViewportBounds = {
        x: -stage.x() / stage.scaleX(),
        y: -stage.y() / stage.scaleY(),
        width: stageSize.width / stage.scaleX(),
        height: stageSize.height / stage.scaleY()
      };
      
      // Validate viewport bounds before setting
      if (isFinite(normalViewportBounds.x) && isFinite(normalViewportBounds.y) &&
          isFinite(normalViewportBounds.width) && isFinite(normalViewportBounds.height) &&
          normalViewportBounds.width > 0 && normalViewportBounds.height > 0) {
        lineBasedFOVRef.current.setViewportBounds(normalViewportBounds);
        
        // Clear FOV cache to ensure fresh calculation with normal bounds
        if (lineBasedFOVRef.current.getFOV && lineBasedFOVRef.current.getFOV().clearCache) {
          lineBasedFOVRef.current.getFOV().clearCache();
        }
      }
    }
    
    // Final FOV update
    if (throttledFOVTimeout.current) {
      clearTimeout(throttledFOVTimeout.current);
      throttledFOVTimeout.current = null;
    }
    if (calculateVisibilityRef.current) {
      visiblePointsRef.current = calculateVisibilityRef.current();
    }
    setFowRenderTrigger(prev => prev + 1); // Only here, to force fog mask re-render if needed
  }, [propTokens, pushCommand, useLineBasedFOV, stageSize]);

  // Handle token selection with multi-select support
  const handleTokenSelect = useCallback((id, event) => {
    try {
      // If we're not using the SELECT tool, switch to it first
      if (activeTool !== TOOLS.SELECT && onToolChange) {
        onToolChange(TOOLS.SELECT);
      }
      
      const ctrlKey = event?.evt?.ctrlKey || event?.evt?.metaKey || false;
      const isDragStart = event?.isDragStart || false;
      const isDoubleClick = event?.isDoubleClick || false;
      onTokenSelect(id, ctrlKey, isDragStart, isDoubleClick);
    } catch (err) {
      console.error('Error in handleTokenSelect:', err);
    }
  }, [onTokenSelect, activeTool, onToolChange]);
  
  // Smart RAF control - only runs when needed
  const rafActiveRef = useRef(false);
  const lastValidPositionRef = useRef({ x: 0, y: 0 });
  const shouldUpdateRAFRef = useRef(false);

  // Group lighting caching for performance
  const groupLightingCacheRef = useRef({}); // Cache keys by token ID
  const groupLightingResultsRef = useRef({}); // Cached FOV results by token ID

  // Update visibility polygon - non-recursive, controlled RAF
  const updateVisibility = useCallback(() => {
    if (!isEditMode && fowVisible && activeTokenRef.current) {
      const now = Date.now();
      const token = activeTokenRef.current;
      
      // Check if position actually changed
      const lastPos = lastValidPositionRef.current;
      const positionChanged = Math.abs(token.x - lastPos.x) > 1 || Math.abs(token.y - lastPos.y) > 1;
      
      // More aggressive throttling during normal operation
      const throttleTime = isDraggingStage ? 50 : 100;
      const timeThresholdMet = visiblePointsRef.current.length === 0 || now - lastUpdateTimeRef.current > throttleTime;
      
      // Only calculate if position changed OR time threshold met
      if ((positionChanged || timeThresholdMet) && timeThresholdMet) {
        if (calculateVisibilityRef.current) {
          visiblePointsRef.current = calculateVisibilityRef.current();
        }
        lastUpdateTimeRef.current = now;
        lastValidPositionRef.current = { x: token.x, y: token.y };
        
        // Only trigger render update if something actually changed
        if (visiblePointsRef.current.length > 0) {
          setFowRenderTrigger(prev => prev + 1);
        }
      }
    }
  }, [isEditMode, fowVisible, isDraggingStage]);

  // Controlled RAF - only starts when needed, stops when not
  const startRAF = useCallback(() => {
    if (!rafActiveRef.current && !isEditMode && fowVisible) {
      rafActiveRef.current = true;
      shouldUpdateRAFRef.current = true;
      
      const rafLoop = () => {
        if (!shouldUpdateRAFRef.current || isEditMode || !fowVisible) {
          rafActiveRef.current = false;
          return;
        }
        
        updateVisibility();
        
        // Continue RAF only if still needed
        if (shouldUpdateRAFRef.current && !isEditMode && fowVisible) {
          rafRef.current = requestAnimationFrame(rafLoop);
        } else {
          rafActiveRef.current = false;
        }
      };
      
      rafRef.current = requestAnimationFrame(rafLoop);
    }
  }, [isEditMode, fowVisible, updateVisibility]);

  // Stop RAF when not needed
  const stopRAF = useCallback(() => {
    shouldUpdateRAFRef.current = false;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    rafActiveRef.current = false;
  }, []);

  // Clean group lighting cache when tokens change
  useEffect(() => {
    // Clear cache when token list changes to prevent stale data
    groupLightingCacheRef.current = {};
    groupLightingResultsRef.current = {};
  }, [propTokens, selectedTokenIds]);

  // Also update visibility when stage position changes
  useEffect(() => {
    if (!isEditMode && fowVisible && activeTokenRef.current) {
      // Only update visiblePointsRef, do not call setFowRenderTrigger here
      if (calculateVisibilityRef.current) {
        visiblePointsRef.current = calculateVisibilityRef.current();
      }
      lastUpdateTimeRef.current = Date.now();
      
      // Update last position to current token position
      lastValidPositionRef.current = { 
        x: activeTokenRef.current.x, 
        y: activeTokenRef.current.y 
      };
      
      // Trigger immediate render update
      setFowRenderTrigger(prev => prev + 1);
      
      // Start RAF if not already running
      startRAF();
    }
  }, [isEditMode, fowVisible, stagePosition, startRAF]); // Use startRAF instead of updateVisibility

  // Handle drag move and end for the stage
  const handleStageDragMove = useCallback(() => {
    if (!isEditMode && fowVisible && activeTokenRef.current) {
      // Set dragging state to true
      setIsDraggingStage(true);
      
      // Update viewport bounds for culling immediately during panning
      if (useLineBasedFOV && lineBasedFOVRef.current && stageRef.current) {
        const stage = stageRef.current;
        const token = activeTokenRef.current;
        const visionRadius = token.visionRadius || 200;
        
        // Calculate current viewport bounds after the stage move
        const currentViewportBounds = {
          x: -stage.x() / stage.scaleX(),
          y: -stage.y() / stage.scaleY(),
          width: stageSize.width / stage.scaleX(),
          height: stageSize.height / stage.scaleY()
        };
        
        // Expand viewport bounds to include token vision area to ensure all relevant walls are included
        const tokenMinX = token.x - visionRadius;
        const tokenMinY = token.y - visionRadius;
        const tokenMaxX = token.x + visionRadius;
        const tokenMaxY = token.y + visionRadius;
        
        const expandedBounds = {
          x: Math.min(currentViewportBounds.x, tokenMinX),
          y: Math.min(currentViewportBounds.y, tokenMinY),
          width: Math.max(currentViewportBounds.x + currentViewportBounds.width, tokenMaxX) - Math.min(currentViewportBounds.x, tokenMinX),
          height: Math.max(currentViewportBounds.y + currentViewportBounds.height, tokenMaxY) - Math.min(currentViewportBounds.y, tokenMinY)
        };
        
        // Validate expanded bounds before setting
        if (isFinite(expandedBounds.x) && isFinite(expandedBounds.y) &&
            isFinite(expandedBounds.width) && isFinite(expandedBounds.height) &&
            expandedBounds.width > 0 && expandedBounds.height > 0) {
          lineBasedFOVRef.current.setViewportBounds(expandedBounds);
          
          // Clear cache to ensure fresh calculation with new bounds
          if (lineBasedFOVRef.current.getFOV && lineBasedFOVRef.current.getFOV().clearCache) {
            lineBasedFOVRef.current.getFOV().clearCache();
          }
        }
      }
      
      const now = Date.now();
      // Force immediate recalculation during dragging to prevent visual artifacts
      if (calculateVisibilityRef.current) {
        visiblePointsRef.current = calculateVisibilityRef.current();
      }
      lastUpdateTimeRef.current = now;
      
      // Update the position state to match actual stage position
      if (stageRef.current) {
        const stage = stageRef.current;
        const currentPosition = { x: stage.x(), y: stage.y() };
        setStagePosition(currentPosition);
      }
    }
  }, [isEditMode, fowVisible, useLineBasedFOV, stageSize.width, stageSize.height]);
  
  // Handle drag end for the stage
  const handleDragEnd = useCallback(() => {
    if (!isEditMode && fowVisible && activeTokenRef.current) {
      // Set dragging state to false
      setIsDraggingStage(false);
      
      // Final viewport bounds update after panning is complete
      if (useLineBasedFOV && lineBasedFOVRef.current && stageRef.current) {
        const stage = stageRef.current;
        const token = activeTokenRef.current;
        const visionRadius = token.visionRadius || 200;
        
        // Calculate final viewport bounds
        const currentViewportBounds = {
          x: -stage.x() / stage.scaleX(),
          y: -stage.y() / stage.scaleY(),
          width: stageSize.width / stage.scaleX(),
          height: stageSize.height / stage.scaleY()
        };
        
        // Expand viewport bounds to include token vision area
        const tokenMinX = token.x - visionRadius;
        const tokenMinY = token.y - visionRadius;
        const tokenMaxX = token.x + visionRadius;
        const tokenMaxY = token.y + visionRadius;
        
        const expandedBounds = {
          x: Math.min(currentViewportBounds.x, tokenMinX),
          y: Math.min(currentViewportBounds.y, tokenMinY),
          width: Math.max(currentViewportBounds.x + currentViewportBounds.width, tokenMaxX) - Math.min(currentViewportBounds.x, tokenMinX),
          height: Math.max(currentViewportBounds.y + currentViewportBounds.height, tokenMaxY) - Math.min(currentViewportBounds.y, tokenMinY)
        };
        
        // Validate and set final bounds
        if (isFinite(expandedBounds.x) && isFinite(expandedBounds.y) &&
            isFinite(expandedBounds.width) && isFinite(expandedBounds.height) &&
            expandedBounds.width > 0 && expandedBounds.height > 0) {
          lineBasedFOVRef.current.setViewportBounds(expandedBounds);
          
          // Clear cache to ensure fresh calculation with final bounds
          if (lineBasedFOVRef.current.getFOV && lineBasedFOVRef.current.getFOV().clearCache) {
            lineBasedFOVRef.current.getFOV().clearCache();
          }
        }
      }
      
      // Do a final visibility calculation and force update
      if (calculateVisibilityRef.current) {
        visiblePointsRef.current = calculateVisibilityRef.current();
      }
      lastUpdateTimeRef.current = Date.now();
      
      // Update the position state to match final stage position
      if (stageRef.current) {
        const stage = stageRef.current;
        const finalPosition = { x: stage.x(), y: stage.y() };
        setStagePosition(finalPosition);
      }
      
      // Force a re-render of the fog of war
      setFowRenderTrigger(prev => prev + 1);
    }
  }, [isEditMode, fowVisible, useLineBasedFOV, stageSize.width, stageSize.height]);

  // Attach the drag move handler to the stage
  useEffect(() => {
    const stage = stageRef.current;
    if (stage) {
      stage.on('dragmove', handleStageDragMove);
      stage.on('dragend', handleDragEnd);
      
      return () => {
        stage.off('dragmove', handleStageDragMove);
        stage.off('dragend', handleDragEnd);
      };
    }
  }, [handleStageDragMove, handleDragEnd]);

  // *** NOW WE CAN USE THE FUNCTIONS ABOVE IN useImperativeHandle ***
  
  // Immediately activate fog when entering play mode - can be called from parent
  useImperativeHandle(
    ref,
    () => ({
      // updateToken, // Potentially remove if MapEditorPage handles all token updates via props
      // deleteToken, // Remove as MapEditorPage handles this
      // getTokens: () => propTokens, // This would return the prop if needed, but parent has it.
      activateFogImmediately: () => {
        setInitialFogCover(true);
        setFowLoading(true);
        setIsTransitioning(true);
      },
      getFOVAdapter: () => {
        return lineBasedFOVRef.current;
      }
    }),
    [/* propTokens */] // Dependency on propTokens if getTokens was kept
  );

  // Automatically enable fog of war when switching to play mode
  useEffect(() => {
    // Skip the first render to avoid unnecessary transitions during initialization
    if (prevEditModeRef.current === undefined) {
      prevEditModeRef.current = isEditMode;
      return;
    }
    
    // If switching from edit mode to play mode
    if (prevEditModeRef.current && !isEditMode) {
      // Immediately cover the entire map with black fog
      setInitialFogCover(true);
      setFowLoading(true);
      setIsTransitioning(true);
      setFowVisible(true);
      
      // If we have selected tokens, use the first one for FOV calculation
      if (selectedTokenIds.length > 0) {
        const token = propTokens.find(t => t.id === selectedTokenIds[0]);
        if (token) {
          activeTokenRef.current = token;
          tokenPositionRef.current = { x: token.x, y: token.y };
          
          // Force immediate wall and visibility calculation
          if (extractWallsRef.current) {
            extractWallsRef.current();
          }
          if (calculateVisibilityRef.current) {
            visiblePointsRef.current = calculateVisibilityRef.current();
          }
          lastUpdateTimeRef.current = Date.now();
          
          // Remove the loading state and initial cover after calculations are complete
          setTimeout(() => {
            setFowLoading(false);
            setInitialFogCover(false);
            setIsTransitioning(false);
          }, 50); // Small delay to ensure rendering completes
        }
              } else {
        // If no tokens are selected, keep the full cover active
        setInitialFogCover(true);
      }
    } else if (!prevEditModeRef.current && isEditMode) {
      // When switching back to edit mode
      setInitialFogCover(false);
      setFowLoading(false);
      setIsTransitioning(false);
    }
    
    // Update the ref for the next render
    prevEditModeRef.current = isEditMode;
  }, [isEditMode, selectedTokenIds, propTokens]); // Removed extractWalls and calculateVisibility to prevent loops

  // Extract walls when needed
  useEffect(() => {
    if (!isEditMode && fowVisible) {
      if (extractWallsRef.current) {
        extractWallsRef.current();
      }
      
      // Force visibility calculation if entering play mode with fog visible
      if (activeTokenRef.current && (!visiblePointsRef.current || visiblePointsRef.current.length === 0)) {
        if (calculateVisibilityRef.current) {
          visiblePointsRef.current = calculateVisibilityRef.current();
        }
        lastUpdateTimeRef.current = Date.now();
      }
    }
  }, [isEditMode, fowVisible, layers]); // Removed extractWalls and calculateVisibility to prevent loops
  
  // Force FOV recalculation when layers change in play mode
  useEffect(() => {
    if (!isEditMode && fowVisible && activeTokenRef.current) {
      // Recalculate walls and visibility when layers change
      if (extractWallsRef.current) {
        extractWallsRef.current();
      }
      if (calculateVisibilityRef.current) {
        visiblePointsRef.current = calculateVisibilityRef.current();
      }
      lastUpdateTimeRef.current = Date.now();
      
      // Force a re-render of the fog of war
      setFowRenderTrigger(prev => prev + 1);
    }
  }, [isEditMode, fowVisible, layers]); // Removed extractWalls and calculateVisibility to prevent loops

  // Store the last dragged token ID to maintain it as active even after drag ends
  const [lastDraggedTokenId, setLastDraggedTokenId] = useState(null);

  // Store references to the latest versions of these functions
  const extractWallsRef = useRef();
  const calculateVisibilityRef = useRef();
  
  // Update refs whenever the functions change
  useEffect(() => {
    extractWallsRef.current = extractWalls;
    calculateVisibilityRef.current = calculateVisibility;
  }, [extractWalls, calculateVisibility]);

  // Update the reference to the active token (prioritize dragged token, then last dragged, then first selected)
  useEffect(() => {
    if (selectedTokenIds.length > 0) {
      // Priority: currently dragged > last dragged (if still selected) > first selected
      let selectedTokenId;
      if (currentlyDraggedTokenId) {
        selectedTokenId = currentlyDraggedTokenId;
      } else if (lastDraggedTokenId && selectedTokenIds.includes(lastDraggedTokenId)) {
        selectedTokenId = lastDraggedTokenId;
      } else {
        selectedTokenId = selectedTokenIds[0];
      }
      
      const tokenDataFromProps = propTokens.find(t => t.id === selectedTokenId);
      if (tokenDataFromProps) {
        const isCurrentlyBeingDragged = currentlyDraggedTokenId === selectedTokenId;
        
        // Prioritize live drag coordinates if this token is being dragged
        const liveDraggedX = (isCurrentlyBeingDragged && activeTokenRef.current?.id === selectedTokenId) 
                               ? activeTokenRef.current.x 
                               : undefined;
        const liveDraggedY = (isCurrentlyBeingDragged && activeTokenRef.current?.id === selectedTokenId) 
                               ? activeTokenRef.current.y 
                               : undefined;

        activeTokenRef.current = {
          ...tokenDataFromProps, // Base data from props
          x: liveDraggedX !== undefined ? liveDraggedX : tokenDataFromProps.x, // Use live drag X if available
          y: liveDraggedY !== undefined ? liveDraggedY : tokenDataFromProps.y, // Use live drag Y if available
        };
        tokenPositionRef.current = { x: activeTokenRef.current.x, y: activeTokenRef.current.y };
        
        // If in play mode with fog visible, force immediate visibility calculation
        if (!isEditMode && fowVisible) {
          // Use refs to avoid dependency issues
          if (extractWallsRef.current) {
            extractWallsRef.current();
          }
          if (calculateVisibilityRef.current) {
            visiblePointsRef.current = calculateVisibilityRef.current();
          }
          lastUpdateTimeRef.current = Date.now();
          setFowRenderTrigger(prev => prev + 1); // Crucial for updating fog after potential activeTokenRef change
        }
      } else { // Token from selectedTokenId not found in propTokens (e.g. deleted)
        activeTokenRef.current = null;
        if (currentlyDraggedTokenId === selectedTokenId) { // Clear drag state if it was for this now-gone token
            setCurrentlyDraggedTokenId(null);
        }
        if (lastDraggedTokenId === selectedTokenId) { // Clear last dragged if it was for this now-gone token
            setLastDraggedTokenId(null);
        }
      }
    } else { // No tokens selected
      activeTokenRef.current = null;
      setCurrentlyDraggedTokenId(null); // Clear drag state if no tokens are selected
      setLastDraggedTokenId(null); // Clear last dragged if no tokens are selected
    }
  }, [selectedTokenIds, propTokens, isEditMode, fowVisible, currentlyDraggedTokenId, lastDraggedTokenId]); // Removed extractWalls and calculateVisibility
  
  // Smart RAF management based on mode changes
  useEffect(() => {
    if (!isEditMode && fowVisible && activeTokenRef.current) {
      // Immediate calculation and start RAF
      updateVisibility();
      startRAF();
    } else {
      // Stop RAF when in edit mode or FOW disabled
      stopRAF();
    }
    
    // Cleanup function
    return () => {
      stopRAF();
    };
  }, [isEditMode, fowVisible, updateVisibility, startRAF, stopRAF]);

  // Memoize the fog of war visibility calculation with extended buffer area
  const fogOfWarMask = useMemo(() => {
    if (!isEditMode) {
      // If we're still loading, return null to keep the initial cover in place
      if (fowLoading) return null;
      
      if (fowVisible && activeTokenRef.current) {
        // Group lighting mode - optimized with position-based caching
        if (groupLightingEnabled && selectedTokenIds.length > 1) {
          const selectedTokens = propTokens.filter(t => selectedTokenIds.includes(t.id));
          
          return (
            <>
              {selectedTokens.map(token => {
                let tokenVisibilityPoints = [];
                
                // Smart FOV calculation with position caching
                if (useLineBasedFOV && lineBasedFOVRef.current) {
                  const visionRadius = token.visionRadius || 200;
                  
                  // Check if this token's position changed significantly since last calculation
                  const cacheKey = `${token.id}_${Math.round(token.x/5)*5}_${Math.round(token.y/5)*5}_${visionRadius}`;
                  const lastCacheKey = groupLightingCacheRef.current[token.id];
                  
                  if (lastCacheKey === cacheKey && groupLightingResultsRef.current[token.id]) {
                    // Use cached result if position hasn't changed significantly (5px grid)
                    tokenVisibilityPoints = groupLightingResultsRef.current[token.id];
                  } else {
                    // Calculate fresh FOV and cache result
                    tokenVisibilityPoints = lineBasedFOVRef.current.calculateVisibility(token.x, token.y, visionRadius);
                    groupLightingCacheRef.current[token.id] = cacheKey;
                    groupLightingResultsRef.current[token.id] = tokenVisibilityPoints;
                  }
                }
                
                // If we have valid FOV points, use them; otherwise fall back to circle
                if (tokenVisibilityPoints && tokenVisibilityPoints.length >= 6) {
                  return (
                    <Line
                      key={`fow-group-${token.id}-${fowRenderTrigger}`}
                      points={tokenVisibilityPoints}
                      closed={true}
                      fill="black"
                      globalCompositeOperation="destination-out"
                      listening={false}
                      perfectDrawEnabled={false}
                      shadowForStrokeEnabled={false}
                    />
                  );
                } else {
                  // Fallback to circle if FOV calculation fails
                  return (
                    <Circle 
                      key={`fow-group-circle-${token.id}-${fowRenderTrigger}`}
                      x={token.x} 
                      y={token.y} 
                      radius={token.visionRadius || 200} 
                      fill="black" 
                      globalCompositeOperation="destination-out" 
                      listening={false} 
                      perfectDrawEnabled={false}
                      shadowForStrokeEnabled={false}
                    />
                  );
                }
              })}
            </>
          );
        }
        
        // Single token mode (existing logic)
        // Use the pre-calculated visibility points
        const points = visiblePointsRef.current;
        
        if (!points || points.length < 6) {
          // Fallback to circle if no valid points
          return (
            <Circle 
              x={activeTokenRef.current.x} 
              y={activeTokenRef.current.y} 
              radius={activeTokenRef.current.visionRadius || 200} 
              fill="black" 
              globalCompositeOperation="destination-out" 
              listening={false} 
              perfectDrawEnabled={false}
              shadowForStrokeEnabled={false}
              key={`fow-circle-${fowRenderTrigger}`} // Add render trigger as key
            />
          );
        }
        
        return (
          <Line
            points={points}
            closed={true}
            fill="black"
            globalCompositeOperation="destination-out"
            listening={false}
            perfectDrawEnabled={false}
            shadowForStrokeEnabled={false}
            key={`fow-${fowRenderTrigger}`} // Use render trigger instead of timestamp
          />
        );
      }
    }
    return null;
  }, [isEditMode, fowVisible, fowLoading, fowRenderTrigger, groupLightingEnabled, selectedTokenIds, propTokens, useLineBasedFOV, lineBasedFOVRef]); // Added missing dependencies

  // Render debug wall segments in edit mode - Commented out: unused variable, debug functionality accessible via renderFovDebugRays()
  // const editModeDebugLayer = useMemo(() => {
  //   if (isEditMode && showDebugFOV) {
  //     return renderEditModeWallSegments();
  //   }
  //   return null;
  // }, [isEditMode, showDebugFOV, renderEditModeWallSegments]); // Simpler dependencies, relies on renderEditModeWallSegments' own deps

  // Handle FOV recalculation when triggered by window focus
  useEffect(() => {
    if (!isEditMode && fowVisible && activeTokenRef.current && fowRenderTrigger > 0) {
      // Use refs to avoid dependency issues
      if (extractWallsRef.current) {
        extractWallsRef.current();
      }
      if (calculateVisibilityRef.current) {
        visiblePointsRef.current = calculateVisibilityRef.current();
      }
      lastUpdateTimeRef.current = Date.now();
    }
  }, [fowRenderTrigger, isEditMode, fowVisible]); // Removed extractWalls and calculateVisibility

  // Handle file selection - Commented out: duplicate functionality, already implemented in LayerManager.js with full UI integration
  // const handleFileChange = async (e) => {
  //   const file = e.target.files[0];
  //   if (file && file.type.match('image.*')) {
  //     const reader = new FileReader();
  //     
  //     reader.onload = async (event) => {
  //       const img = new window.Image();
  //       img.src = event.target.result;
  //       
  //       img.onload = async () => {
  //         setMapImage(img);
  //         
  //         try {
  //           // Get active layer to determine upload strategy
  //           const activeLayer = getActiveLayer();
  //           let targetLayer;
  //           
  //           if (activeLayer && activeLayer.is_wall_layer) {
  //             // If active layer is FOW, find the paired image layer in the same group
  //             const layerGroup = getLayerGroup ? getLayerGroup(activeLayer.layer_id) : null;
  //             
  //             if (layerGroup) {
  //               // Find the image layer in the same group
  //               const imageLayerInGroup = layers.find(layer => 
  //                 layerGroup.layers.includes(layer.layer_id) && !layer.is_wall_layer
  //               );
  //               
  //               if (imageLayerInGroup) {
  //                 // Use the existing image layer from the same group
  //                 targetLayer = imageLayerInGroup;
  //               } else {
  //                 // If no image layer in group, use the first available image layer
  //                 targetLayer = layers.find(layer => !layer.is_wall_layer);
  //               }
  //             } else {
  //               // No group found, use the first available image layer
  //               targetLayer = layers.find(layer => !layer.is_wall_layer);
  //             }
  //             
  //             // If still no target layer found, fallback to creating one
  //             if (!targetLayer) {
  //               targetLayer = await createImageLayer(
  //                 file.name.replace(/\.[^/.]+$/, "") || "Maps Layer"
  //               );
  //             }
  //           } else if (activeLayer && !activeLayer.is_wall_layer) {
  //             // If active layer is image layer, use it
  //             targetLayer = activeLayer;
  //           } else {
  //             // No active layer - this shouldn't happen with default layers, but handle gracefully
  //             targetLayer = layers.find(layer => !layer.is_wall_layer);
  //             
  //             if (!targetLayer) {
  //               // Fallback: create a new image layer if somehow none exists
  //               targetLayer = await createImageLayer(
  //                 file.name.replace(/\.[^/.]+$/, "") || "Maps Layer"
  //               );
  //             }
  //           }
  //           
  //           if (targetLayer) {
  //             // Create container instead of direct image
  //             const newContainer = await addContainer({
  //               name: file.name.replace(/\.[^/.]+$/, "") || "New Map",
  //               layerId: targetLayer.layer_id,
  //               image: {
  //                 src: event.target.result,
  //                 width: img.width,
  //                 height: img.height
  //               },
  //               x: 0,
  //               y: 0,
  //               scaleX: 1,
  //               scaleY: 1,
  //               rotation: 0
  //             });
  //             
  //             if (newContainer) {
  //               // Auto-select the new container
  //               setSelectedContainerId(newContainer.id);
  //               
  //               // Reset position and scale to show the new image
  //               setStagePosition({ x: 0, y: 0 });
  //               setStageScale(1);
  //             }
  //           }
  //         } catch (error) {
  //           console.error('[MapDisplay] Error creating container from uploaded image:', error);
  //           
  //           // Fallback to old method if container creation fails
  //           const layerData = {
  //             layer_name: file.name.replace(/\.[^/.]+$/, "") || "Background Image",
  //             layer_data: JSON.stringify({ 
  //               lines: [],
  //               image: {
  //                 src: event.target.result,
  //                 width: img.width,
  //                 height: img.height,
  //                 x: 0,
  //                 y: 0
  //               } 
  //             }),
  //             is_wall_layer: false,
  //             layer_order: layers.length,
  //             is_visible: true
  //           };
  //           
  //           createLayer(layerData);
  //           setStagePosition({ x: 0, y: 0 });
  //           setStageScale(1);
  //         }
  //       };
  //     };
  //     
  //     reader.readAsDataURL(file);
  //   }
  // };

  // Handle zoom
  const handleWheel = (e) => {
    e.evt.preventDefault();
    
    const scaleBy = 1.1;
    const stage = stageRef.current;
    const pointer = stage.getPointerPosition();
    
    const mousePointTo = {
      x: (pointer.x - stage.x()) / stage.scaleX(),
      y: (pointer.y - stage.y()) / stage.scaleY()
    };
    
    // Calculate new scale
    let newScale = e.evt.deltaY < 0 ? stageScale * scaleBy : stageScale / scaleBy;
    
    // Limit min and max scale - allow much wider zoom out for large maps
    newScale = Math.max(0.05, Math.min(newScale, 10));
    
    // Calculate new position
    const newPos = {
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale
    };
    
    // Save references to current segment data BEFORE any updates (safely)
    let currentSegments = [];
    try {
      if (useLineBasedFOV && lineBasedFOVRef.current && 
          lineBasedFOVRef.current.getSegmentCollection && 
          lineBasedFOVRef.current.getSegmentCollection().segments) {
        // Make a deep copy of the current segments to preserve them
        currentSegments = lineBasedFOVRef.current.getSegmentCollection().segments.map(
          segment => ({ ...segment })
        );
      }
    } catch (err) {
      console.error('Error preserving segments during zoom:', err);
    }
    
    // Update state immediately
    setStageScale(newScale);
    setStagePosition(newPos);
    
    // Safely handle FOV updates
    try {
      if (useLineBasedFOV && lineBasedFOVRef.current) {
        // Update viewport bounds with the new scale/position
        let viewportBounds = {
          x: -newPos.x / newScale,
          y: -newPos.y / newScale,
          width: stageSize.width / newScale,
          height: stageSize.height / newScale
        };
        
        // If we have an active token, expand bounds to include its vision radius
        if (!isEditMode && fowVisible && activeTokenRef.current) {
          const token = activeTokenRef.current;
          const visionRadius = token.visionRadius || 200;
          
          const tokenMinX = token.x - visionRadius;
          const tokenMinY = token.y - visionRadius;
          const tokenMaxX = token.x + visionRadius;
          const tokenMaxY = token.y + visionRadius;
          
          viewportBounds = {
            x: Math.min(viewportBounds.x, tokenMinX),
            y: Math.min(viewportBounds.y, tokenMinY),
            width: Math.max(viewportBounds.x + viewportBounds.width, tokenMaxX) - Math.min(viewportBounds.x, tokenMinX),
            height: Math.max(viewportBounds.y + viewportBounds.height, tokenMaxY) - Math.min(viewportBounds.y, tokenMinY)
          };
        }
        
        // Apply new viewport bounds
        lineBasedFOVRef.current.setViewportBounds(viewportBounds);
        
        // Safely restore segments if needed
        if (currentSegments.length > 0 && 
            lineBasedFOVRef.current.getSegmentCollection && 
            lineBasedFOVRef.current.getSegmentCollection().segments) {
          
          const currentCollection = lineBasedFOVRef.current.getSegmentCollection();
          if (currentCollection.segments.length !== currentSegments.length) {
            // Restore segments only if count mismatch happened
            currentCollection.segments = [...currentSegments];
            if (lineBasedFOVRef.current.fov && typeof lineBasedFOVRef.current.fov.setWalls === 'function') {
              lineBasedFOVRef.current.fov.setWalls(currentSegments);
            }
          }
        }
        
        // Smart recalculation if in play mode - throttled and position-aware
        if (!isEditMode && fowVisible && activeTokenRef.current) {
          const now = performance.now();
          const token = activeTokenRef.current;
          const lastPos = lastValidPositionRef.current;
          
          // Check if position changed meaningfully or enough time passed for zoom/scale changes
          const positionChanged = Math.abs(token.x - lastPos.x) > 2 || Math.abs(token.y - lastPos.y) > 2;
          const timeThreshold = 150; // Less frequent updates for zoom events (was immediate)
          const shouldUpdate = positionChanged || (now - lastUpdateTimeRef.current > timeThreshold);
          
          if (shouldUpdate) {
            // Recalculate visibility and update the render trigger
            visiblePointsRef.current = calculateVisibility();
            setFowRenderTrigger(prev => prev + 1);
            lastUpdateTimeRef.current = now;
            lastValidPositionRef.current = { x: token.x, y: token.y };
          }
        }
      }
    } catch (err) {
      console.error('Error updating FOV during zoom:', err);
    }
  };

  // Extract viewport bounds update into a separate function
  const updateViewportBounds = useCallback((position = stagePosition, scale = stageScale) => {
    if (useLineBasedFOV && lineBasedFOVRef.current && lineBasedFOVRef.current.getFOV) {
      const viewportBounds = {
        x: -position.x / scale,
        y: -position.y / scale,
        width: stageSize.width / scale,
        height: stageSize.height / scale
      };
      lineBasedFOVRef.current.getFOV().setViewportBounds(viewportBounds);
    }
  }, [useLineBasedFOV, stageSize, stagePosition, stageScale]);

  // Update viewport bounds on component mount and when relevant props change
  useEffect(() => {
    updateViewportBounds(stagePosition, stageScale);
  }, [updateViewportBounds, stagePosition, stageScale]);

  // handleStageDragMove is defined earlier in the file

  // Update FOV quality when it changes - only when needed
  useEffect(() => {
    if (lineBasedFOVRef.current && useLineBasedFOV && !isEditMode && fowVisible && activeTokenRef.current) {
      visiblePointsRef.current = calculateVisibility();
      setFowRenderTrigger(prev => prev + 1);
      
      // Update position cache to sync with current calculation
      const token = activeTokenRef.current;
      lastValidPositionRef.current = { x: token.x, y: token.y };
    }
  }, [fovQuality, useLineBasedFOV, isEditMode, fowVisible, calculateVisibility]);

  // Update fog visibility based on edit mode - fog is always on in play mode, off in edit mode
  useEffect(() => {
    setFowVisible(!isEditMode);
  }, [isEditMode]);
  
  // Add function to handle quality change
  // Removed quality change handler - quality is now fixed at 720 rays

  // Removed debug mode toggle - debug mode controls removed

  // Add function to render debug rays
  const renderFovDebugRays = () => {
    if (!showDebugFOV || !activeTokenRef.current || !lineBasedFOVRef.current || !useLineBasedFOV) {
      return null;
    }
    
    const token = activeTokenRef.current;
    const fov = lineBasedFOVRef.current.getFOV();
    
    // If FOV has a getRaycastDebugInfo method, use it
    if (typeof fov.getRaycastDebugInfo === 'function') {
      const debugInfo = fov.getRaycastDebugInfo();
      if (!debugInfo || !debugInfo.rays) return null;
      
      return (
        <>
          {/* Draw rays */}
          {debugInfo.rays.map((ray, i) => (
            <Line
              key={`debug-ray-${i}`}
              points={[token.x, token.y, ray.endX, ray.endY]}
              stroke={ray.hit ? "rgba(255,0,0,0.3)" : "rgba(0,255,0,0.2)"}
              strokeWidth={1}
              dash={ray.hit ? [] : [5, 5]}
              listening={false}
            />
          ))}
          
          {/* Draw intersection points */}
          {debugInfo.intersections && debugInfo.intersections.map((point, i) => (
            <Circle
              key={`debug-intersection-${i}`}
              x={point.x}
              y={point.y}
              radius={3}
              fill={point.isBlocking ? "red" : "yellow"}
              listening={false}
            />
          ))}
        </>
      );
    }
    
    return null;
  };

  // Add effect to update the viewport bounds for culling when stage properties change
  // (This is disabled during token dragging to avoid conflicts - viewport bounds are handled in handleTokenDragMove)
  useEffect(() => {
    if (useLineBasedFOV && lineBasedFOVRef.current && stageRef.current && !currentlyDraggedTokenId) {
      let viewportBounds = {
        x: -stagePosition.x / stageScale,
        y: -stagePosition.y / stageScale,
        width: stageSize.width / stageScale,
        height: stageSize.height / stageScale
      };
      
      // If we have an active token in play mode, expand bounds to include its vision radius
      if (!isEditMode && fowVisible && activeTokenRef.current) {
        const token = activeTokenRef.current;
        const visionRadius = token.visionRadius || 200;
        
        const tokenMinX = token.x - visionRadius;
        const tokenMinY = token.y - visionRadius;
        const tokenMaxX = token.x + visionRadius;
        const tokenMaxY = token.y + visionRadius;
        
        viewportBounds = {
          x: Math.min(viewportBounds.x, tokenMinX),
          y: Math.min(viewportBounds.y, tokenMinY),
          width: Math.max(viewportBounds.x + viewportBounds.width, tokenMaxX) - Math.min(viewportBounds.x, tokenMinX),
          height: Math.max(viewportBounds.y + viewportBounds.height, tokenMaxY) - Math.min(viewportBounds.y, tokenMinY)
        };
      }
      
      lineBasedFOVRef.current.setViewportBounds(viewportBounds);
      
      // Smart FOV recalculation when viewport bounds change in play mode
      if (!isEditMode && fowVisible && activeTokenRef.current) {
        const token = activeTokenRef.current;
        const lastPos = lastValidPositionRef.current;
        const now = Date.now();
        
        // Check if position changed or enough time passed since last update
        const positionChanged = Math.abs(token.x - lastPos.x) > 3 || Math.abs(token.y - lastPos.y) > 3;
        const timeThreshold = 200; // Throttle viewport bounds updates to 200ms
        const shouldUpdate = positionChanged || (now - lastUpdateTimeRef.current > timeThreshold);
        
        if (shouldUpdate) {
          // Clear cache to ensure fresh calculation with new bounds
          if (lineBasedFOVRef.current.getFOV && lineBasedFOVRef.current.getFOV().clearCache) {
            lineBasedFOVRef.current.getFOV().clearCache();
          }
          
          // Recalculate visibility with new bounds
          visiblePointsRef.current = calculateVisibility();
          setFowRenderTrigger(prev => prev + 1);
          lastUpdateTimeRef.current = now;
          lastValidPositionRef.current = { x: token.x, y: token.y };
        }
      }
    }
  }, [useLineBasedFOV, stagePosition, stageScale, stageSize, isEditMode, fowVisible, calculateVisibility, currentlyDraggedTokenId]);

  // Helper function to save current shape to active layer
  const saveCurrentShape = useCallback((toolType, points = null, dimensions = null) => {
    const activeLayer = getActiveLayer();
    if (!activeLayer) return;
    
    try {
      let layerData = JSON.parse(activeLayer.layer_data || '{"lines":[]}');
      if (!layerData.lines) layerData.lines = [];
      
      // Store the previous state for undo
      const previousLayerData = JSON.parse(JSON.stringify(layerData));

      if (toolType === TOOLS.ERASER) {
        // Use the state from before erasing started if available
        const startingState = eraserStartStateRef.current || previousLayerData;

        // Parse current layer state (may already include continuous erase updates)
        let currentState;
        try {
          currentState = JSON.parse(activeLayer.layer_data || '{"lines":[]}');
          if (!currentState.lines) currentState.lines = [];
        } catch (e) {
          currentState = { lines: [] };
        }

        // Apply a final destructive erase pass using the full stroke path
        // This is idempotent if continuous erase already applied changes
        const finalLines = eraseLinesDestructive(
          currentState.lines,
          { points: points },
          toolOptions?.eraserSize || 20
        );

        const finalState = { ...currentState, lines: finalLines };

        // Determine if anything changed relative to the stroke's starting state
        const hasChanges = JSON.stringify(finalState) !== JSON.stringify(startingState);

        // Persist the final state to the layer
        if (JSON.stringify(finalState) !== JSON.stringify(currentState)) {
          updateLayer(activeLayer.layer_id, {
            ...activeLayer,
            layer_data: JSON.stringify(finalState)
          });
        }

        // Push a single ERASE command that captures the entire stroke
        if (hasChanges) {
          try {
            pushCommand({
              type: COMMAND_TYPES.ERASE,
              targetId: activeLayer.layer_id,
              previousState: startingState,
              newState: finalState,
              description: `Erased/modified items`
            });
          } catch (error) {
            console.error(`[ERASE] Error pushing command to history:`, error);
          }
        }

        // Clear the eraser start state
        eraserStartStateRef.current = null;

        return; // End eraser branch
      }
      
      // Get the appropriate thickness based on the tool type
      const getStrokeWidth = () => {
        if (toolType === TOOLS.ERASER) return toolOptions?.eraserSize || 20;
        if (toolType === TOOLS.PENCIL) return toolOptions?.brushSize || 5;
        if (toolType === TOOLS.WALL) return toolOptions?.wallThickness || 3;
        if (toolType === TOOLS.POLYGON || toolType === TOOLS.RECTANGLE || toolType === TOOLS.CIRCLE) {
          return toolOptions?.wallThickness || 3;
        }
        return 2; // default
      };

      // Create new shape based on tool type
      let newShape = {
        id: Date.now().toString(),
        tool: toolType,
        color: toolOptions?.color || '#000000',
        strokeWidth: getStrokeWidth()
      };
      
      // Set isLOSObject based on shapeBlockMode for shapes
      if ((toolType === TOOLS.POLYGON || toolType === TOOLS.RECTANGLE || toolType === TOOLS.CIRCLE) && 
          activeLayer.is_wall_layer) {
        newShape.isLOSObject = toolOptions?.shapeBlockMode === 'twoHitLOS';
      }
      
      // Add specific properties based on tool type
      if (points) {
        if (toolType === TOOLS.POLYGON) {
          newShape.points = Array.isArray(points[0]) ? points.flat() : points;
        } else {
          newShape.points = points;
        }
      }
      
      if (dimensions) {
        Object.assign(newShape, dimensions);
      }
      
      // Add new shape to layer
      layerData.lines.push(newShape);
      
      // Update layer with new data
      updateLayer(activeLayer.layer_id, {
        ...activeLayer,
        layer_data: JSON.stringify(layerData)
      });
      
      // Push to history
      const commandType = (toolType === TOOLS.PENCIL || toolType === TOOLS.WALL) 
        ? COMMAND_TYPES.DRAW_LINE 
        : COMMAND_TYPES.DRAW_SHAPE;
      
      pushCommand({
        type: commandType,
        targetId: activeLayer.layer_id,
        previousState: previousLayerData,
        newState: layerData,
        description: `Drew ${toolType.toLowerCase()}`
      });
    } catch (err) {
      console.error('Error saving shape:', err);
    }
  }, [getActiveLayer, updateLayer, toolOptions, pushCommand]);

  // Mouse event handlers
  const handleMouseDown = useCallback((e) => {
    // Allow all drawing tools to work in both edit and play modes
    
    // Get mouse position relative to the stage
    const stage = e.target.getStage();
    const pointerPos = stage.getPointerPosition();
    const x = (pointerPos.x - stage.x()) / stage.scaleX();
    const y = (pointerPos.y - stage.y()) / stage.scaleY();
    
    // Handle right mouse button - always switch to SELECT tool and prepare for potential rectangular selection
    if (e.evt.button === 2) {
      e.evt.preventDefault(); // Prevent context menu
      
      // Switch to SELECT tool if not already active
      if (activeTool !== TOOLS.SELECT && onToolChange) {
        onToolChange(TOOLS.SELECT);
      }
      
      // Store the right click start position but don't start selection yet
      setRightClickStart({ x, y, ctrlKey: e.evt.ctrlKey || e.evt.metaKey });
      return;
    }
    
    // Check if we clicked on a token when token tool is active
    if (activeTool === TOOLS.TOKEN) {
      // Only handle left mouse button for token creation/selection
      // Allow middle mouse button (button 1) and right mouse button (button 2) to pass through for panning
      if (e.evt.button !== 0) {
        return; // Let the event bubble up for panning or other actions
      }
      
      // Check if the clicked target is a token or part of a token group
      let target = e.target;
      let isClickOnToken = false;
      
      // Traverse up the node hierarchy to find if we clicked on a token
      while (target && target !== stage) {
        if (target.name && target.name() === 'token-group') {
          isClickOnToken = true;
          break;
        }
        target = target.parent;
      }
      
      // If we clicked on an existing token, don't create a new one
      if (isClickOnToken) {
        setIsDrawing(false);
        return;
      }
      
      // Otherwise, create a new token at the clicked position (only for left click)
      if (onNewTokenCreated) {
        const tokenSize = toolOptions?.tokenSize || 30;
        const tokenColor = toolOptions?.color || '#cccccc';
        const newTokenId = `token-${Date.now()}`;
        
        const newToken = {
          id: newTokenId,
          x: x,
          y: y,
          radius: tokenSize / 2,
          fill: tokenColor,
          name: `Token ${propTokens.length + 1}`,
          visionRadius: tokenSize * 5, // Default vision radius based on token size
          initiative: 15, // Default initiative value
          zIndex: propTokens.length, // Place on top of other tokens
          isPC: true // Default to PC (Player Character)
        };
        
        onNewTokenCreated(newToken);
      }
      setIsDrawing(false); // End drawing immediately for tokens
      return;
    }
    
    // For other tools, also check for left mouse button
    if (e.evt.button !== 0) {
      return; // Allow middle and right mouse buttons to pass through for panning
    }
    
    setIsDrawing(true);
    setCurrentMousePos({ x, y });
    // Container drawing disabled: nothing to reset
    
    if (activeTool === TOOLS.WALL) {
      // Start a new wall line
      setWallPoints([x, y]);
    } else if (activeTool === TOOLS.PENCIL || activeTool === TOOLS.ERASER) {
      const newLine = {
        tool: activeTool,
        color: toolOptions?.color || '#000000',
        strokeWidth: activeTool === TOOLS.ERASER 
          ? (toolOptions?.eraserSize || 20) 
          : (toolOptions?.brushSize || 5),
        points: [x, y]
      };
      setCurrentLine(newLine);
      
      // For eraser, store the initial state before any erasing happens
      if (activeTool === TOOLS.ERASER) {
        const activeLayer = getActiveLayer();
        if (activeLayer) {
          try {
            const layerData = JSON.parse(activeLayer.layer_data || '{"lines":[]}');
            eraserStartStateRef.current = JSON.parse(JSON.stringify(layerData));
          } catch (err) {
            console.error('Error storing eraser start state:', err);
          }
        }
      }
    } else if (activeTool === TOOLS.POLYGON) {
      // Add point to polygon
      if (currentPolygonPoints.length === 0) {
        // Starting a new polygon
        setCurrentPolygonPoints([[x, y]]);
        setCurrentPolygonLines([]);
        setIsDrawing(true);
      } else {
        // Check if click is near starting point to close polygon
        const startPoint = currentPolygonPoints[0];
        const dist = Math.sqrt(Math.pow(startPoint[0] - x, 2) + Math.pow(startPoint[1] - y, 2));
        
        if (dist < 10 && currentPolygonPoints.length > 2) {
          // Close polygon and finish drawing
          const points = [...currentPolygonPoints, [startPoint[0], startPoint[1]]].flat();
          saveCurrentShape(TOOLS.POLYGON, points);
          setCurrentPolygonPoints([]);
          setCurrentPolygonLines([]);
          setIsDrawing(false);
        } else {
          // Add new point and create a new line segment
          const lastPoint = currentPolygonPoints[currentPolygonPoints.length - 1];
          
          // Add the new line segment
          const newLine = {
            points: [lastPoint[0], lastPoint[1], x, y],
            color: toolOptions?.color || '#000000',
            strokeWidth: toolOptions?.wallThickness || 3
          };
          
          setCurrentPolygonLines([...currentPolygonLines, newLine]);
          setCurrentPolygonPoints([...currentPolygonPoints, [x, y]]);
        }
      }
    } else if (activeTool === TOOLS.RECTANGLE) {
      // Start rectangle
      setCurrentRectangleStartPoint({ x, y });
    } else if (activeTool === TOOLS.CIRCLE) {
      // Start circle
      setCurrentCircleCenter({ x, y });
      setCurrentCircleRadius(0);
    }
  }, [activeTool, toolOptions, currentPolygonPoints, onNewTokenCreated, propTokens, currentPolygonLines, saveCurrentShape, onToolChange, getActiveLayer]);

  const handleMouseMove = useCallback((e) => {
    // Allow all drawing tools to work in both edit and play modes
    
    // Get mouse position relative to the stage
    const stage = e.target.getStage();
    const pointerPos = stage.getPointerPosition();
    const x = (pointerPos.x - stage.x()) / stage.scaleX();
    const y = (pointerPos.y - stage.y()) / stage.scaleY();
    
    setCurrentMousePos({ x, y });
    
    // Check if we should start rectangular selection from right click
    if (rightClickStart && !isRectSelecting) {
      const deltaX = Math.abs(x - rightClickStart.x);
      const deltaY = Math.abs(y - rightClickStart.y);
      const threshold = 5; // Minimum movement to start selection
      
      if (deltaX > threshold || deltaY > threshold) {
        // Start rectangular selection
        setIsRectSelecting(true);
        setRectSelectionStart(rightClickStart);
        setRectSelectionEnd({ x, y });
        setRightClickStart(null); // Clear the right click start
      }
      return;
    }
    
    // Handle rectangular selection dragging
    if (isRectSelecting) {
      setRectSelectionEnd({ x, y });
      
      // Live selection - select tokens as we drag the rectangle
      if (rectSelectionStart && onTokenSelect) {
        const minX = Math.min(rectSelectionStart.x, x);
        const maxX = Math.max(rectSelectionStart.x, x);
        const minY = Math.min(rectSelectionStart.y, y);
        const maxY = Math.max(rectSelectionStart.y, y);
        
        // Find tokens within the current selection rectangle
        const tokensInRect = propTokens.filter(token => 
          token.x >= minX && token.x <= maxX && 
          token.y >= minY && token.y <= maxY
        );
        
        if (tokensInRect.length > 0) {
          // If Ctrl was held when starting selection, add to existing selection
          if (rectSelectionStart.ctrlKey) {
            // Combine current selection with tokens in rectangle
            const currentlySelected = new Set(selectedTokenIds);
            tokensInRect.forEach(token => currentlySelected.add(token.id));
            onTokenSelect(Array.from(currentlySelected));
          } else {
            // Replace selection with tokens in rectangle
            onTokenSelect(tokensInRect.map(t => t.id));
          }
        } else if (!rectSelectionStart.ctrlKey) {
          // If no tokens in rectangle and not adding to selection, clear selection
          onTokenSelect([]);
        }
      }
      return;
    }
    
    if (!isDrawing) {
      // Check if we should show the eraser preview
      if (activeTool === TOOLS.ERASER) {
        setShowEraserPreview(true);
      } else {
        setShowEraserPreview(false);
      }
      return;
    }
    
    if (activeTool === TOOLS.PENCIL || activeTool === TOOLS.ERASER) {
      // Add point to current line
      const newPoints = [...currentLine.points, x, y];
      setCurrentLine({ ...currentLine, points: newPoints });
      
      // For eraser, apply continuous erasing with throttling
      if (activeTool === TOOLS.ERASER) {
        const now = Date.now();
        // Only apply continuous erase if we have enough movement (more than just start/end points)
        if (now - lastEraserApplyTimeRef.current > 200 && newPoints.length > 4) { // Need more than 2 points for continuous erase
          // Apply eraser effect immediately
          const activeLayer = getActiveLayer();
          if (activeLayer) {
            try {
              let layerData = JSON.parse(activeLayer.layer_data || '{"lines":[]}');
              if (!layerData.lines) layerData.lines = [];
              
              // Perform destructive erase with current eraser path
              layerData.lines = eraseLinesDestructive(
                layerData.lines,
                { points: newPoints },
                toolOptions?.eraserSize || 20
              );
              
              // Update layer immediately (but don't save to history during continuous erase)
              updateLayer(activeLayer.layer_id, {
                ...activeLayer,
                layer_data: JSON.stringify(layerData)
              });
              
              lastEraserApplyTimeRef.current = now;
            } catch (err) {
              console.error('Error applying continuous erase:', err);
            }
          }
        }
      }
    } else if (activeTool === TOOLS.WALL) {
      // Wall preview follows mouse
      // (Wall points are updated on mouse up)
    } else if (activeTool === TOOLS.POLYGON && currentPolygonPoints.length > 0) {
      // Just update the current mouse position for the preview line
      setCurrentMousePos({ x, y });
    } else if (activeTool === TOOLS.RECTANGLE) {
      // Update rectangle dimensions
    } else if (activeTool === TOOLS.CIRCLE) {
      // Update circle radius
      if (currentCircleCenter) {
        const dx = x - currentCircleCenter.x;
        const dy = y - currentCircleCenter.y;
        const radius = Math.sqrt(dx * dx + dy * dy);
        setCurrentCircleRadius(radius);
      }
    }
  }, [activeTool, isDrawing, currentLine, toolOptions, currentPolygonPoints, getActiveLayer, updateLayer, selectedTokenIds, propTokens, onTokenSelect, rectSelectionStart, rightClickStart, isRectSelecting, currentCircleCenter]);

  const handleMouseUp = useCallback((e) => {
    // Handle right click without drag - just clear the right click start
    if (rightClickStart && e.evt.button === 2) {
      e.evt.preventDefault(); // Prevent context menu
      setRightClickStart(null);
      return;
    }
    
    // Handle rectangular selection completion
    if (isRectSelecting) {
      e.evt.preventDefault(); // Prevent context menu
      setIsRectSelecting(false);
      
      // Clear selection rectangle state
      setRectSelectionStart(null);
      setRectSelectionEnd(null);
      return;
    }
    
    if (!isDrawing) return;
    
    // Get mouse position relative to the stage
    const stage = e.target.getStage();
    const pointerPos = stage.getPointerPosition();
    const x = (pointerPos.x - stage.x()) / stage.scaleX();
    const y = (pointerPos.y - stage.y()) / stage.scaleY();
    
    if (activeTool === TOOLS.WALL) {
      // Complete wall if length > 0
      if (wallPoints.length === 2) {
        // Second point completes the wall
        const dx = x - wallPoints[0];
        const dy = y - wallPoints[1];
        if (dx * dx + dy * dy > 5) { // Minimum length check
          saveCurrentShape(TOOLS.WALL, [...wallPoints, x, y]);
        }
        setWallPoints([]);
        } else {
        // Record first point
        setWallPoints([x, y]);
        setIsDrawing(false); 
        return; // Keep drawing mode on until second point
      }
    } else if (activeTool === TOOLS.PENCIL || activeTool === TOOLS.ERASER) {
      // Save current line
      // For PENCIL: need more than 2 points for a meaningful line
      // For ERASER: even 2 points (single click) should be saved if it caused changes
      const minPoints = activeTool === TOOLS.ERASER ? 2 : 2; // Both need at least 2 points
      
      // Draw only to layers (container drawings disabled)
      if (currentLine && currentLine.points.length >= minPoints) {
        saveCurrentShape(activeTool, currentLine.points);
      } else if (activeTool === TOOLS.ERASER && currentLine && currentLine.points.length === 2) {
        // Special case: quick eraser click with exactly 2 points (start and end at same/close position)
        const activeLayer = getActiveLayer();
        if (activeLayer && eraserStartStateRef.current) {
          try {
            let layerData = JSON.parse(activeLayer.layer_data || '{"lines":[]}');
            if (!layerData.lines) layerData.lines = [];
            
            // Apply erase at the click position
            const originalLines = [...layerData.lines];
            layerData.lines = eraseLinesDestructive(
              layerData.lines,
              { points: currentLine.points },
              toolOptions?.eraserSize || 20
            );
            
            // Check if anything changed
            const hasChanges = JSON.stringify(originalLines) !== JSON.stringify(layerData.lines);
            
            if (hasChanges) {
              // Update layer
              updateLayer(activeLayer.layer_id, {
                ...activeLayer,
                layer_data: JSON.stringify(layerData)
              });
              
              // Save to history
              pushCommand({
                type: COMMAND_TYPES.ERASE,
                targetId: activeLayer.layer_id,
                previousState: eraserStartStateRef.current,
                newState: layerData,
                description: `Erased items (quick click)`
              });
            }
          } catch (err) {
            console.error('Error applying quick erase:', err);
          }
        }
      } else {
        // For ERASER: even if we don't have enough points for a line, 
        // we might have made changes during continuous erase - save those changes
        if (activeTool === TOOLS.ERASER && eraserStartStateRef.current) {
          const activeLayer = getActiveLayer();
          if (activeLayer) {
            try {
              const currentLayerData = JSON.parse(activeLayer.layer_data || '{"lines":[]}');
              const originalData = eraserStartStateRef.current;
              
              // Check if anything actually changed
              const hasChanges = JSON.stringify(currentLayerData) !== JSON.stringify(originalData);
              
              if (hasChanges) {
                // Save the changes that were made during continuous erase
                pushCommand({
                  type: COMMAND_TYPES.ERASE,
                  targetId: activeLayer.layer_id,
                  previousState: originalData,
                  newState: currentLayerData,
                  description: `Erased items (quick click)`
                });
              }
            } catch (err) {
              console.error('Error checking eraser changes:', err);
            }
          }
        }
      }
      setCurrentLine(null);
      // Container drawing disabled: nothing to reset
      // After an eraser operation, clear the eraser highlight
      if (activeTool === TOOLS.ERASER) {
        setShowEraserPreview(false); // Also hide preview circle just in case
        // Clear eraser start state if not already cleared
        eraserStartStateRef.current = null;
      }
    } else if (activeTool === TOOLS.RECTANGLE) {
      if (currentRectangleStartPoint) {
        // Save rectangle
        const width = Math.abs(x - currentRectangleStartPoint.x);
        const height = Math.abs(y - currentRectangleStartPoint.y);
        
        if (width > 5 && height > 5) { // Minimum size check
          const rectX = Math.min(currentRectangleStartPoint.x, x);
          const rectY = Math.min(currentRectangleStartPoint.y, y);
          
          saveCurrentShape(TOOLS.RECTANGLE, null, {
            x: rectX, y: rectY, width, height
          });
        }
        setCurrentRectangleStartPoint(null);
      }
    } else if (activeTool === TOOLS.CIRCLE) {
      if (currentCircleCenter && currentCircleRadius > 5) {
        // Save circle
        saveCurrentShape(TOOLS.CIRCLE, null, {
          x: currentCircleCenter.x,
          y: currentCircleCenter.y,
          radius: currentCircleRadius
        });
      }
      setCurrentCircleCenter(null);
      setCurrentCircleRadius(0);
    }
    
    setIsDrawing(false);
  }, [activeTool, isDrawing, wallPoints, currentLine, currentRectangleStartPoint, currentCircleCenter, currentCircleRadius, saveCurrentShape, getActiveLayer, pushCommand, updateLayer, toolOptions, rightClickStart, isRectSelecting]);

  const handleMouseEnter = useCallback(() => {
    // Handle mouse entering the canvas area
    if (activeTool === TOOLS.ERASER) {
      setShowEraserPreview(true);
    }
  }, [activeTool]);

  const handleMouseLeave = useCallback(() => {
    // Handle mouse leaving the canvas area
    setShowEraserPreview(false);
    
    // Optional: cancel current drawing operation
    if (isDrawing && activeTool !== TOOLS.POLYGON) {
        setIsDrawing(false);
        setCurrentLine(null);
      setCurrentRectangleStartPoint(null);
      setCurrentCircleCenter(null);
        // If eraser was active and drawing is cancelled, clear its state too
        if (activeTool === TOOLS.ERASER) {
          eraserStartStateRef.current = null;
        }
    }
  }, [isDrawing, activeTool]);

  // Add a clearCache method to the LineBasedFOV class if it doesn't exist
  useEffect(() => {
    // Add clearCache method to the FOV instance if it doesn't exist
    if (useLineBasedFOV && lineBasedFOVRef.current && lineBasedFOVRef.current.getFOV()) {
      const fov = lineBasedFOVRef.current.getFOV();
      if (!fov.clearCache) {
        fov.clearCache = function() {
          // Clear any internal caches that might be causing artifacts
          this._cachedVisibility = null;
          this._lastCalculationParams = null;
          this._geometryCache = null;
          
          // Clear internal algorithm-specific caches if they exist
          if (this._rayCache) this._rayCache = {};
          if (this._segmentCache) this._segmentCache = {};
          if (this._polygonCache) this._polygonCache = [];
          
          // If there's a segment collection with a cache, clear that too
          if (this.segments && typeof this.segments.clearCache === 'function') {
            this.segments.clearCache();
          }
        };
      }
    }
  }, [useLineBasedFOV]);

  // Add useEffect to update FOV whenever layers change
  useEffect(() => {
    if (useLineBasedFOV && lineBasedFOVRef.current) {
      lineBasedFOVRef.current.clearCache();
      lineBasedFOVRef.current.updateWallsFromLayers(layers);
    }
  }, [layers, useLineBasedFOV]);

  // Removed unused distance helper function - duplicate of coordinateUtils.distance which is already available

  // Removed unused segmentNearEraser function - replaced by segmentNearAnyEraserCircle which is actively used

  // Removed unused segmentErasedFraction function - legacy approximation method replaced by precise segmentCircleIntersections

  // Removed unused projectPointOnSegment function - functionality available in WallSegment.distanceToPoint()

  // segmentCircleIntersections - moved to file scope for stable reference

  // clipWallSegmentByEraser - moved to file scope for stable reference

  // eraseLinesDestructive - moved to file scope for stable reference

  // NOTE: reconstructOriginalLines function was removed completely
  // It was an unused advanced undo optimization feature that was never implemented
  // The function attempted to reconstruct original lines from clipped segments 
  // but caused ESLint issues with unused destructuring variables

  // segmentNearAnyEraserCircle - moved to file scope for stable reference

  // clipLineByEraser - moved to file scope for stable reference

  // NOTE: updateImageInLayer function was removed
  // It was part of the legacy image system that has been replaced by the Container system
  // All image functionality is now handled through MapContainer and ContainerHoverMenu components

  // Helper to delete image from layerData
  const deleteImageFromLayer = useCallback((imgId) => {
    const activeLayer = getActiveLayer();
    if (!activeLayer) return;
    let layerData = JSON.parse(activeLayer.layer_data || '{"lines":[]}');
    if (!layerData.lines) layerData.lines = [];
    layerData.lines = layerData.lines.filter(line => !(line.type === 'image' && line.id === imgId));
    updateLayer(activeLayer.layer_id, {
      ...activeLayer,
      layer_data: JSON.stringify(layerData)
    });
    setSelectedImageId(null);
  }, [getActiveLayer, updateLayer]);

  // Handle Delete key for image deletion
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedImageId && (e.key === 'Delete' || e.key === 'Backspace')) {
        deleteImageFromLayer(selectedImageId);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImageId, deleteImageFromLayer]);

  // NOTE: toggleLockImage function was removed
  // Legacy image lock functionality has been replaced by Container system
  // Use toggleLock from ContainerContext for modern container-based images

  // NOTE: bringImageToFront function was removed
  // Legacy image z-index functionality has been replaced by Container system
  // Use bringToFront from ContainerContext for modern container-based images
  // NOTE: sendImageToBack function was removed
  // Legacy image z-index functionality has been replaced by Container system
  // Use sendToBack from ContainerContext for modern container-based images

  // Legacy context menu handlers - removed as part of container migration

  // Legacy image helper components - removed as part of container migration
  // These have been replaced by the new MapContainer and ContainerHoverMenu components

  // Keyboard shortcuts for eraser size (removed mouse wheel)
  useEffect(() => {
    if (!isEditMode || activeTool !== TOOLS.ERASER) return;
    const handleKeyDown = (e) => {
      if (activeTool === TOOLS.ERASER) {
        let newSize = toolOptions.eraserSize || 20;
        if (e.key === '[') {
          newSize = Math.max(5, newSize - 2);
          onOptionChange('eraserSize', newSize);
        } else if (e.key === ']') {
          newSize = Math.min(100, newSize + 2);
          onOptionChange('eraserSize', newSize);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEditMode, activeTool, toolOptions.eraserSize, onOptionChange]);
  
  // Keyboard shortcuts for Undo/Redo
  useEffect(() => {
    if (!isEditMode) return; // Only in edit mode
    
    const handleKeyDown = (e) => {
      // Ctrl+Z or Cmd+Z for Undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        const command = undo();
        if (command) {
          // Apply the undo
          const layer = layers.find(l => l.layer_id === command.targetId);
          if (layer) {
            updateLayer(command.targetId, {
              ...layer,
              layer_data: JSON.stringify(command.previousState)
            });
            
            // Clear FOV cache if needed
            if (useLineBasedFOV && lineBasedFOVRef.current) {
              lineBasedFOVRef.current.clearCache();
            }
          }
        }
      }
      // Ctrl+Y or Ctrl+Shift+Z or Cmd+Shift+Z for Redo
      else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        const command = redo();
        if (command) {
          // Apply the redo
          const layer = layers.find(l => l.layer_id === command.targetId);
          if (layer) {
            updateLayer(command.targetId, {
              ...layer,
              layer_data: JSON.stringify(command.newState)
            });
            
            // Clear FOV cache if needed
            if (useLineBasedFOV && lineBasedFOVRef.current) {
              lineBasedFOVRef.current.clearCache();
            }
          }
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditMode, undo, redo, layers, updateLayer, useLineBasedFOV]);

  // ESC key handler for canceling polygon drawing
  useEffect(() => {
    if (!isEditMode || activeTool !== TOOLS.POLYGON) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && currentPolygonPoints.length > 0) {
        e.preventDefault();
        // Cancel current polygon drawing
        setCurrentPolygonPoints([]);
        setCurrentPolygonLines([]);
        setIsDrawing(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditMode, activeTool, currentPolygonPoints.length]);

  // Pre-compute layer data to prevent excessive getLayerData calls during rendering
  const layersWithData = useMemo(() => {
    return layers.map(layer => ({
      ...layer,
      layerData: getLayerData(layer.layer_id)
    }));
  }, [layers, getLayerData]);

  // Pre-compute legacy images for rendering
  const legacyImages = useMemo(() => {
    return layersWithData.flatMap(layer => {
      if (layer.layerData.image && !layer.layerData.migrated) {
        return [{
          key: `img-${layer.layer_id}`,
          src: layer.layerData.image.src,
          x: layer.layerData.image.x || 0,
          y: layer.layerData.image.y || 0,
          width: layer.layerData.image.width || 0,
          height: layer.layerData.image.height || 0
        }];
      }
      return [];
    });
  }, [layersWithData]);

  // Helper function to zoom to center of viewport
  const zoomToCenter = useCallback((newScale) => {
    if (!stageRef.current) return;
    
    const currentScale = stageScale;
    
    // Get the center point of the current viewport
    const centerX = stageSize.width / 2;
    const centerY = stageSize.height / 2;
    
    // Calculate the point in the stage coordinates that corresponds to the viewport center
    const stageCenterX = (centerX - stagePosition.x) / currentScale;
    const stageCenterY = (centerY - stagePosition.y) / currentScale;
    
    // Calculate new position to keep the same center point
    const newPosition = {
      x: centerX - stageCenterX * newScale,
      y: centerY - stageCenterY * newScale
    };
    
    setStageScale(newScale);
    setStagePosition(newPosition);
  }, [stageScale, stagePosition, stageSize]);

  // Helper function to reset view to default
  const resetView = useCallback(() => {
    setStageScale(1);
    setStagePosition({ x: 0, y: 0 });
  }, []);

  // Helper function to fit all content to screen
  const fitToScreen = useCallback(() => {
    if (!stageRef.current) return;
    
    // Find all visible containers, layers, tokens, and drawings to calculate bounds
    let minX = 0, minY = 0, maxX = 0, maxY = 0;
    let hasContent = false;
    
    // Helper function to update bounds
    const updateBounds = (x, y, width = 0, height = 0) => {
      const itemMaxX = x + width;
      const itemMaxY = y + height;
      
      if (!hasContent) {
        minX = x;
        minY = y;
        maxX = itemMaxX;
        maxY = itemMaxY;
        hasContent = true;
      } else {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, itemMaxX);
        maxY = Math.max(maxY, itemMaxY);
      }
    };
    
    // Check containers
    containers.forEach(container => {
      if (container.isVisible !== false && container.image) {
        const { x = 0, y = 0, scaleX = 1, scaleY = 1, rotation = 0 } = container;
        const baseWidth = container.image.width || 0;
        const baseHeight = container.image.height || 0;
        
        // Calculate actual dimensions with scale
        const scaledWidth = baseWidth * scaleX;
        const scaledHeight = baseHeight * scaleY;
        
        // If there's rotation, calculate the bounding box
        if (rotation !== 0) {
          const radians = rotation * Math.PI / 180;
          const cos = Math.abs(Math.cos(radians));
          const sin = Math.abs(Math.sin(radians));
          
          // Calculate the bounding box after rotation
          const rotatedWidth = scaledWidth * cos + scaledHeight * sin;
          const rotatedHeight = scaledWidth * sin + scaledHeight * cos;
          
          // For rotated objects, we need to consider the center point
          const centerX = x + scaledWidth / 2;
          const centerY = y + scaledHeight / 2;
          
          updateBounds(
            centerX - rotatedWidth / 2,
            centerY - rotatedHeight / 2,
            rotatedWidth,
            rotatedHeight
          );
        } else {
          // No rotation, simple case
          updateBounds(x, y, scaledWidth, scaledHeight);
        }
      }
    });
    
    // Check layers for legacy images
    layersWithData.forEach(layer => {
      if (layer.isVisible && layer.layerData.image && !layer.layerData.migrated) {
        const img = layer.layerData.image;
        const x = img.x || 0;
        const y = img.y || 0;
        const width = img.width || 0;
        const height = img.height || 0;
        updateBounds(x, y, width, height);
      }
    });
    
    // Check tokens - use current propTokens from closure
    propTokens?.forEach(token => {
      const { x = 0, y = 0, radius = 25 } = token;
      // For tokens, we consider the bounding box of the circle
      updateBounds(x - radius, y - radius, radius * 2, radius * 2);
    });
    
    // Check drawings in all layers
    layersWithData.forEach(layer => {
      if (layer.is_visible !== false && layer.layerData.lines) {
        layer.layerData.lines.forEach(line => {
          if (line.tool === TOOLS.PENCIL || line.tool === TOOLS.WALL) {
            // For lines, check all points
            if (line.points && line.points.length >= 2) {
              for (let i = 0; i < line.points.length; i += 2) {
                const x = line.points[i];
                const y = line.points[i + 1];
                if (typeof x === 'number' && typeof y === 'number') {
                  updateBounds(x, y, 0, 0);
                }
              }
            }
          } else if (line.tool === TOOLS.RECTANGLE) {
            // For rectangles
            const { x = 0, y = 0, width = 0, height = 0 } = line;
            updateBounds(x, y, width, height);
          } else if (line.tool === TOOLS.CIRCLE) {
            // For circles
            const { x = 0, y = 0, radius = 0 } = line;
            updateBounds(x - radius, y - radius, radius * 2, radius * 2);
          } else if (line.tool === TOOLS.POLYGON) {
            // For polygons, check all points
            if (line.points && line.points.length >= 2) {
              for (let i = 0; i < line.points.length; i += 2) {
                const x = line.points[i];
                const y = line.points[i + 1];
                if (typeof x === 'number' && typeof y === 'number') {
                  updateBounds(x, y, 0, 0);
                }
              }
            }
          }
        });
      }
    });
    
    if (!hasContent) {
      // No content found, reset to default
      resetView();
      return;
    }
    
    // Debug: Log the calculated bounds
    console.log('Fit to Screen - Calculated bounds:', {
      minX, minY, maxX, maxY,
      contentWidth: maxX - minX,
      contentHeight: maxY - minY
    });
    
    // Calculate content dimensions
    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;
    const contentCenterX = minX + contentWidth / 2;
    const contentCenterY = minY + contentHeight / 2;
    
    // Calculate scale to fit content with some padding
    const padding = 50; // 50px padding on all sides
    const availableWidth = stageSize.width - padding * 2;
    const availableHeight = stageSize.height - padding * 2;
    
    const scaleX = availableWidth / contentWidth;
    const scaleY = availableHeight / contentHeight;
    const newScale = Math.min(scaleX, scaleY, 2); // Don't zoom in too much
    
    // Calculate position to center content
    const newPosition = {
      x: stageSize.width / 2 - contentCenterX * newScale,
      y: stageSize.height / 2 - contentCenterY * newScale
    };
    
    setStageScale(newScale);
    setStagePosition(newPosition);
  }, [containers, layersWithData, stageSize, resetView, propTokens]);

  // Helper function to zoom in
  const zoomIn = useCallback(() => {
    const newScale = Math.min(stageScale * 1.2, 10);
    zoomToCenter(newScale);
  }, [stageScale, zoomToCenter]);

  // Helper function to zoom out
  const zoomOut = useCallback(() => {
    const newScale = Math.max(stageScale / 1.2, 0.05);
    zoomToCenter(newScale);
  }, [stageScale, zoomToCenter]);

  return (
    <div className={`map-display ${isEditMode ? 'edit-mode' : 'play-mode'}`} ref={containerRef}>
      {/* Full-screen black cover that appears immediately during mode transitions */}
      {isTransitioning && (
        <div className="immediate-fog-cover" style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: '#000',
          zIndex: 10
        }} />
        )}
        
        {/* Removed fog and debug control buttons */}
        
      {/* Only render the stage when not transitioning, or in edit mode */}
      <div className="stage-container" data-tool={activeTool.toLowerCase()} style={{ visibility: isTransitioning ? 'hidden' : 'visible' }}>
        <div className="zoom-controls">
          <button 
            onClick={zoomIn}
            className="zoom-btn zoom-in"
            title="Zoom In"
          >
            <i className="fas fa-plus"></i>
          </button>
          <button 
            onClick={zoomOut}
            className="zoom-btn zoom-out"
            title="Zoom Out"
          >
            <i className="fas fa-minus"></i>
          </button>
          <button 
            onClick={fitToScreen}
            className="zoom-btn zoom-fit"
            title="Fit to Screen"
          >
            <i className="fas fa-expand-arrows-alt"></i>
          </button>
          <button 
            onClick={resetView}
            className="zoom-btn zoom-reset"
            title="Reset View (1:1)"
          >
            <i className="fas fa-redo-alt"></i>
          </button>
      </div>
      
        {/* Auto-lock notification */}
        {isEditMode && shouldAutoLock() && (
          <div className="auto-lock-notification" style={{
            position: 'absolute',
            top: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'var(--primary-color-70)',
            color: 'var(--text-color)',
            padding: '8px 16px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: 'bold',
            zIndex: 5,
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            border: '2px solid var(--accent-color)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <i className="fas fa-lock" style={{ fontSize: '12px' }}></i>
            Images auto-locked while FOW layer is active - Drawing enabled
          </div>
        )}
      
        <Stage
          width={stageSize.width}
          height={stageSize.height}
          onWheel={handleWheel}
          scaleX={stageScale}
          scaleY={stageScale}
          x={stagePosition.x}
          y={stagePosition.y}
          draggable={activeTool === TOOLS.SELECT || activeTool === TOOLS.TOKEN}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onDragMove={handleStageDragMove}
          onDragEnd={handleDragEnd}
          onContextMenu={(e) => {
            e.evt.preventDefault(); // Always prevent context menu
            // Right click functionality is handled in onMouseDown
          }}
          ref={stageRef}
        >
          {/* 1. Background Layer - Legacy images (will be migrated) */}
          <Layer listening={false}>
            {legacyImages.map(img => (
              <KonvaImage
                key={img.key}
                image={(() => {
                  const imageObj = new window.Image();
                  imageObj.src = img.src;
                  return imageObj;
                })()}
                x={img.x}
                y={img.y}
                width={img.width}
                height={img.height}
                listening={false}
              />
            ))}
          </Layer>

          {/* 2. Main Content Layer - All layers combined into Groups for performance */}
          <Layer>
            {/* Render layers in correct order as Groups instead of separate Layers */}
            {layersWithData
              .filter(layer => layer.is_visible !== false)
              .sort((a, b) => a.layer_order - b.layer_order)
              .map(layer => (
                <Group key={`layer-group-${layer.layer_id}`}>
                  {/* Render containers for this layer */}
                  {getContainersByLayer(layer.layer_id)
                    .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
                    .map(container => (
                      <MapContainer
                        key={container.id}
                        container={container}
                        isSelected={selectedContainerId === container.id}
                        onUpdate={handleContainerUpdate}
                        onSelect={handleContainerSelect}
                        // Container drawings disabled
                        isDrawingMode={activeTool !== TOOLS.SELECT}
                        activeTool={activeTool}
                        onHoverStart={handleContainerHoverStart}
                        onHoverEnd={handleContainerHoverEnd}
                        showTransformer={isEditMode}
                        isAutoLocked={shouldAutoLock() && isEditMode}
                        isEditMode={isEditMode}
                        showHoverMenu={hoveredContainerId === container.id}
                        onMenuAction={handleContainerMenuAction}
                        viewport={{
                          x: -stagePosition.x / stageScale,
                          y: -stagePosition.y / stageScale,
                          width: stageSize.width / stageScale,
                          height: stageSize.height / stageScale,
                          scale: stageScale
                        }}
                      />
                    ))}
                  
                  {/* Render drawings for this layer */}
                  <Group key={`drawings-${layer.layer_id}`} visible={areDrawingsVisible}>
                    {(layer.layerData.lines || []).map((line, i) => {
                      if (line.tool === TOOLS.WALL) {
                        return (
                          <Line
                            key={`${layer.layer_id}-wall-${i}`}
                            points={line.points}
                            stroke={line.color}
                            strokeWidth={line.strokeWidth}
                            tension={0}
                            lineCap="round"
                            lineJoin="round"
                            listening={false}
                          />
                        );
                      } else if (line.tool === TOOLS.PENCIL) {
                        return (
                          <Line
                            key={`${layer.layer_id}-pencil-${i}`}
                            points={line.points}
                            stroke={line.color}
                            strokeWidth={line.strokeWidth}
                            tension={0.5}
                            lineCap="round"
                            lineJoin="round"
                            listening={false}
                          />
                        );
                      } else if (line.tool === TOOLS.RECTANGLE) {
                        return (
                          <Rect
                            key={`${layer.layer_id}-rect-${i}`}
                            x={line.x}
                            y={line.y}
                            width={line.width}
                            height={line.height}
                            stroke={line.color}
                            strokeWidth={line.strokeWidth}
                            listening={false}
                            fill={line.isLOSObject ? 'rgba(128, 128, 128, 0.2)' : undefined}
                            dash={line.isLOSObject ? [5, 5] : undefined}
                          />
                        );
                      } else if (line.tool === TOOLS.CIRCLE) {
                        return (
                          <Circle
                            key={`${layer.layer_id}-circle-${i}`}
                            x={line.x}
                            y={line.y}
                            radius={line.radius}
                            stroke={line.color}
                            strokeWidth={line.strokeWidth}
                            listening={false}
                            fill={line.isLOSObject ? 'rgba(128, 128, 128, 0.2)' : undefined}
                            dash={line.isLOSObject ? [5, 5] : undefined}
                          />
                        );
                      } else if (line.tool === TOOLS.POLYGON) {
                        return (
                          <Line
                            key={`${layer.layer_id}-polygon-${i}`}
                            points={line.points}
                            stroke={line.color}
                            strokeWidth={line.strokeWidth}
                            closed={true}
                            tension={0}
                            lineCap="round"
                            lineJoin="round"
                            listening={false}
                            fill={line.isLOSObject ? 'rgba(128, 128, 128, 0.2)' : undefined}
                            dash={line.isLOSObject ? [5, 5] : undefined}
                          />
                        );
                      }
                      return null;
                    })}
                  </Group>
                </Group>
              ))}
            

          </Layer>

          {/* 3. Preview/Temporary Drawing Layer - Always on top */}
          <Layer listening={false} visible={areDrawingsVisible}>
            {/* רינדור קווי הפוליגון הזמניים בזמן ציור */}
            {activeTool === TOOLS.POLYGON && currentPolygonLines.map((line, i) => (
              <Line
                key={`polygon-preview-${i}`}
                points={line.points}
                stroke={line.color || 'var(--secondary-color)'}
                strokeWidth={line.strokeWidth || 2}
                dash={[6, 6]}
                opacity={0.7}
                listening={false}
              />
            ))}
            {/* Preview line for next polygon segment - shows from first point */}
            {activeTool === TOOLS.POLYGON && currentPolygonPoints.length > 0 && (
              <Line
                points={[
                  currentPolygonPoints[currentPolygonPoints.length - 1][0],
                  currentPolygonPoints[currentPolygonPoints.length - 1][1],
                  currentMousePos.x,
                  currentMousePos.y
                ]}
                stroke={toolOptions?.color || '#000000'}
                strokeWidth={toolOptions?.wallThickness || 3}
                dash={[4, 4]}
                opacity={0.5}
                listening={false}
              />
            )}
            {/* Starting point indicator for polygon - always visible when polygon can be closed */}
            {activeTool === TOOLS.POLYGON && currentPolygonPoints.length > 2 && (
              <Circle
                x={currentPolygonPoints[0][0]}
                y={currentPolygonPoints[0][1]}
                radius={8}
                stroke={toolOptions?.color || '#000000'}
                strokeWidth={2}
                fill="rgba(255, 255, 255, 0.5)"
                listening={false}
              />
            )}
            {/* Preview Rectangle while drawing */}
            {activeTool === TOOLS.RECTANGLE && currentRectangleStartPoint && isDrawing && (
              <Rect
                x={Math.min(currentRectangleStartPoint.x, currentMousePos.x)}
                y={Math.min(currentRectangleStartPoint.y, currentMousePos.y)}
                width={Math.abs(currentMousePos.x - currentRectangleStartPoint.x)}
                height={Math.abs(currentMousePos.y - currentRectangleStartPoint.y)}
                stroke={toolOptions?.color || '#000000'}
                strokeWidth={toolOptions?.wallThickness || 3}
                dash={[4, 4]}
                opacity={0.5}
                listening={false}
              />
            )}
            
            {/* Preview Circle while drawing */}
            {activeTool === TOOLS.CIRCLE && currentCircleCenter && isDrawing && currentCircleRadius > 0 && (
              <Circle
                x={currentCircleCenter.x}
                y={currentCircleCenter.y}
                radius={currentCircleRadius}
                stroke={toolOptions?.color || '#000000'}
                strokeWidth={toolOptions?.wallThickness || 3}
                dash={[4, 4]}
                opacity={0.5}
                listening={false}
              />
            )}
            
            {/* Current line being drawn (Pencil tool) */}
            {currentLine && activeTool === TOOLS.PENCIL && (
              <Line
                points={currentLine.points}
                stroke={currentLine.color}
                strokeWidth={currentLine.strokeWidth}
                tension={0.5}
                lineCap="round"
                lineJoin="round"
                listening={false}
                opacity={0.8}
              />
            )}
            
            {/* Current eraser path being drawn */}
            {currentLine && activeTool === TOOLS.ERASER && (
              <Line
                points={currentLine.points}
                stroke="rgba(217, 150, 59, 0.6)"
                strokeWidth={currentLine.strokeWidth}
                tension={0.5}
                lineCap="round"
                lineJoin="round"
                listening={false}
                opacity={0.7}
              />
            )}
            
            {/* Current wall being drawn (Wall tool) */}
            {activeTool === TOOLS.WALL && wallPoints.length === 2 && (
                  <Line
                points={[...wallPoints, currentMousePos.x, currentMousePos.y]}
                stroke={toolOptions?.color || '#000000'}
                strokeWidth={toolOptions?.wallThickness || 3}
                    tension={0}
                    lineCap="round"
                    lineJoin="round"
                    listening={false}
                opacity={0.8}
                dash={[4, 4]}
                  />
                )}
                
            {/* Improved Eraser Preview Circle */}
            {showEraserPreview && activeTool === TOOLS.ERASER && (
              <>
                  <Circle
                  x={currentMousePos.x}
                  y={currentMousePos.y}
                  radius={(toolOptions?.eraserSize || 20) / 2}
                  stroke="var(--secondary-color)" // Using theme secondary color
                  strokeWidth={2}
                  fill="rgba(217, 150, 59, 0.15)" // Subtle secondary color fill with transparency
                    listening={false}
                  shadowBlur={isDrawing ? 6 : 0} // Only show glow when actually erasing
                  shadowColor="var(--secondary-color)"
                  shadowOpacity={isDrawing ? 0.5 : 0}
                />
                <Text
                  x={currentMousePos.x + (toolOptions?.eraserSize || 20) / 2 + 8}
                  y={currentMousePos.y - 10}
                  text={`${toolOptions?.eraserSize || 20}px`}
                  fontSize={14}
                  fill="var(--secondary-color)"
                  fontFamily="var(--font-secondary)"
                    listening={false}
                  shadowBlur={2}
                  shadowColor="rgba(0, 0, 0, 0.5)"
                  />
              </>
            )}
          </Layer>
          
          {/* 4. Tokens Layer */}
          <Layer>
            {propTokens.map(token => (
              <Token
                key={token.id}
                id={token.id}
                x={token.x}
                y={token.y}
                radius={token.radius}
                fill={token.fill}
                name={token.name}
                image={token.image}
                visionRadius={token.visionRadius}
                onDragMove={handleTokenDragMove}
                onDragEnd={handleTokenDragEnd}
                onClick={handleTokenSelect}
                onActualDragStart={handleTokenActualDragStart}
                isSelected={selectedTokenIds.includes(token.id)}
                isDraggable={activeTool === TOOLS.SELECT || activeTool === TOOLS.TOKEN}
                stageScale={stageScale}
              />
            ))}
            
            {/* Rectangular selection preview */}
            {isRectSelecting && rectSelectionStart && rectSelectionEnd && (
              <Rect
                x={Math.min(rectSelectionStart.x, rectSelectionEnd.x)}
                y={Math.min(rectSelectionStart.y, rectSelectionEnd.y)}
                width={Math.abs(rectSelectionEnd.x - rectSelectionStart.x)}
                height={Math.abs(rectSelectionEnd.y - rectSelectionStart.y)}
                stroke="#3498db"
                strokeWidth={2}
                dash={[5, 5]}
                fill="rgba(52, 152, 219, 0.1)"
                listening={false}
              />
            )}
          </Layer>
          
          {/* 5. Fog of War Layer */}
          {!isEditMode && (
            <Layer listening={false}>
              <Rect
                x={-50000}
                y={-50000}
                width={100000}
                height={100000}
                fill="rgba(0, 0, 0, 0.95)"
                perfectDrawEnabled={false}
                shadowForStrokeEnabled={false}
                listening={false}
              />
              {initialFogCover ? null : fogOfWarMask}
            </Layer>
          )}
          
          {/* 6. Debug Layer (optional) */}
          {showDebugFOV && (
            <Layer listening={false}>
              {renderFovDebugRays()}
            </Layer>
          )}
        </Stage>
        
        {!mapImage && layers.length === 0 && (
          <div className="upload-prompt">
            <p>Upload a map from the Layers panel to get started</p>
          </div>
        )}
      </div>
    </div>
  );
});

export default MapDisplay; 